import { api } from './client'
import { ApiError } from './types'
import { MASKED_360_PREFIX } from '../lib/media'
import { canPurchaseSeats } from '../lib/seatPlan'
import { canJoinAskaPlus, type AskaPlusMembership, type AskaPlusPlan } from '../lib/askaPlus'
import type { SourcingOrderView, SourcingLimitBasis } from '../lib/sourcing'
import type { StoneVerdict } from '../lib/verification'
import type {
  Diamond,
  SearchFilters,
  SearchResponse,
  CartItem,
  Order,
  UserClaims,
  PaymentTerms,
  AdminOrderRow,
  Fulfillment,
  FulfillmentStatus,
  QcRoute,
  ShareView,
} from './types'
import { SAMPLE_STONES } from '../lib/sampleStones'

/**
 * Client-side fallback search. When the Cloud Functions backend
 * isn't deployed yet (Blaze plan pending), the server returns 404 on
 * /api/search. We catch that and run the same filter logic locally
 * against bundled sample data so the deployed catalog still works.
 *
 * Once Functions are live, the API responds 200 and this never fires.
 * Pure client-side, mirrors the server's filter logic in
 * functions/src/search/filter.ts.
 */
function localSearch(stones: Diamond[], f: SearchFilters): SearchResponse {
  let rows = [...stones]
  const includesCI = (arr: string[] | undefined, v: string | null) =>
    !!arr?.length && !!v && arr.some((x) => x.toLowerCase() === v.toLowerCase())
  if (f.shape?.length)    rows = rows.filter((s) => includesCI(f.shape, s.shape))
  if (f.color?.length)    rows = rows.filter((s) => includesCI(f.color, s.color))
  if (f.clarity?.length)  rows = rows.filter((s) => includesCI(f.clarity, s.clarity))
  if (f.cut?.length)      rows = rows.filter((s) => includesCI(f.cut, s.cut))
  if (typeof f.caratMin === 'number') rows = rows.filter((s) => s.carat >= f.caratMin!)
  if (typeof f.caratMax === 'number') rows = rows.filter((s) => s.carat <= f.caratMax!)
  // Null prices (locked for this caller) filter/sort as 0 — mirrors the server.
  if (typeof f.priceMin === 'number') rows = rows.filter((s) => (s.priceUSD ?? 0) >= f.priceMin!)
  if (typeof f.priceMax === 'number') rows = rows.filter((s) => (s.priceUSD ?? 0) <= f.priceMax!)
  if (f.stockIds?.length) {
    const set = new Set(f.stockIds.map((s) => s.toUpperCase()))
    rows = rows.filter((s) => set.has(s.stockId.toUpperCase()))
  }
  switch (f.sortBy) {
    case 'carat_asc':  rows.sort((a, b) => a.carat - b.carat); break
    case 'carat_desc': rows.sort((a, b) => b.carat - a.carat); break
    case 'price_asc':  rows.sort((a, b) => (a.priceUSD ?? 0) - (b.priceUSD ?? 0)); break
    case 'price_desc': rows.sort((a, b) => (b.priceUSD ?? 0) - (a.priceUSD ?? 0)); break
    default:
      rows.sort((a, b) => (b.carat - a.carat) || ((a.pricePerCarat ?? 0) - (b.pricePerCarat ?? 0)))
  }
  return { total: rows.length, results: rows, top3: rows.slice(0, 3), appliedFilters: f }
}

/**
 * Endpoint surface — one function per route. Co-locating these makes
 * the public API of the platform legible at a glance, and keeps the
 * fetch shape consistent across pages. Pages import from here, not
 * from `client.ts` directly.
 */

export const searchApi = {
  // NOTE: no `anonymous: true` — the search must carry the Firebase ID
  // token when the buyer is signed in, or the server strips supplier
  // contact / storefront fields and a signed-in supplier sees a
  // contact-less catalog. The token provider awaits authReady, so the
  // first fetch on page load waits for session restore before firing.
  query: async (filters: SearchFilters): Promise<SearchResponse> => {
    try {
      return await api<SearchResponse>('/search', { method: 'POST', body: filters })
    } catch (err) {
      // Only fall back to bundled demo data when the backend is
      // genuinely unreachable (network / DNS / CORS → TypeError). A real
      // server response (4xx/5xx) is surfaced to the UI so filters that
      // legitimately fail don't silently show fake stones. The demo
      // fallback carries `isDemoFallback` so the page can warn the buyer
      // they're looking at cached data, not live inventory.
      if (err instanceof TypeError) {
        return { ...localSearch(SAMPLE_STONES, filters), isDemoFallback: true }
      }
      throw err
    }
  },
  // Accepts a uniqueId (preferred — the real doc id) or a stockId (TagNo, which
  // the server resolves to the first matching stone). Callers pass
  // String(uniqueId ?? stockId).
  detail: async (idOrStockId: string): Promise<Diamond> => {
    try {
      return await api<Diamond>(`/search/${encodeURIComponent(idOrStockId)}`)
    } catch (err) {
      // Same rule as query: only substitute the bundled sample when the
      // backend is unreachable; a 4xx/5xx is surfaced to the caller.
      if (err instanceof TypeError) {
        const found = SAMPLE_STONES.find(
          (s) => s.stockId.toUpperCase() === idOrStockId.toUpperCase()
            || (s.uniqueId != null && String(s.uniqueId) === idOrStockId),
        )
        if (found) return found
      }
      throw err
    }
  },
}

/**
 * Signal capture for the recommender. Both routes are `requireAuth`, so
 * callers must only fire these for signed-in users. Fire-and-forget: a
 * failed track (401, network, 400) must NEVER surface to the UI — the
 * `.catch(() => {})` swallows everything and callers `void` the result.
 */
export const trackApi = {
  // Stone-detail open → users/{uid}/recentlyViewed (feeds "viewedNotBought").
  view: (stockId: string, source = 'detail'): void => {
    void api('/track/view', { method: 'POST', body: { stockId, source } }).catch(() => {})
  },
  // Search run → analytics search_results (feeds "matchesPastSearches").
  // `filters` MUST be a JSON string — the server schema is strict and the
  // recommender JSON.parses it back into SearchFilters.
  search: (filters: SearchFilters): void => {
    void api('/track', {
      method: 'POST',
      body: { type: 'search_results', filters: JSON.stringify(filters) },
    }).catch(() => {})
  },
}

/** Per-user "Recommended for you" from /api/recommendations (the interest algo;
 *  distinct from global buzz — see docs/RECOMMENDATIONS-AND-BUZZ.md). Ranked +
 *  media-filtered; `personalized` is false only for the zero-signal cold start. */
export interface RecommendationsResponse {
  recommended: Diamond[]
  personalized: boolean
}

export const recommendationsApi = {
  // Signed-in only (route is requireAuth). Prices/refStockId stay gated by
  // applyMarketplace server-side exactly like /search.
  get: (limit = 12): Promise<RecommendationsResponse> =>
    api<RecommendationsResponse>(`/recommendations?limit=${limit}`),
}

export const cartApi = {
  list: () => api<{ items: CartItem[] }>('/cart'),
  // Prefer the uniqueId (`id`) so duplicate TagNos add the right physical
  // stone; fall back to the legacy stockId shape when there's no uniqueId
  // (bundled sample data). Backend accepts both.
  // A 409 `stone_unavailable` here is the JIT live-stock check refusing to put
  // a stone the supplier has already sold into a cart. On the success path the
  // response also carries `verification`, so a reprice discovered at add time
  // can be shown right where the buyer clicked.
  add: (stockId: string, uniqueId?: number | null) =>
    api<{ items: CartItem[]; verification?: StoneVerdict | null }>('/cart/add', {
      method: 'POST',
      body: uniqueId != null ? { id: String(uniqueId) } : { stockId },
    }),
  remove: (itemId: string) =>
    api<{ items: CartItem[] }>('/cart/remove', { method: 'POST', body: { itemId } }),
  clear: () => api<{ items: CartItem[] }>('/cart/clear', { method: 'POST' }),
}

/**
 * Just-in-time live-stock verification for whatever is on screen.
 *
 * ONE endpoint for the cart drawer/sheet, the mobile stone detail and the
 * pull-to-refresh on browse — all three ask the same question. Takes OUR ids
 * (uniqueId or stockId), never a supplier ref; the server answers per stone and
 * includes price deltas only for a KYC-approved caller.
 *
 * Bounded at 40 ids server-side (a screenful), because each one costs a lookup.
 */
export const stonesApi = {
  verify: (stockIds: string[]) =>
    api<{ verdicts: StoneVerdict[]; checkedAt: string }>('/stones/verify', {
      method: 'POST',
      body: { stockIds: stockIds.slice(0, 40) },
    }),
}

/**
 * DiaEx 360° viewer proxy. Resolves a supplier Vision360 viewer URL into a
 * frame sequence served through OUR domain (/api/v/img) so the Diaex360
 * player can render a native drag-to-rotate spin — no supplier host / ref
 * leak, no iframe. `resolved:false` ⇒ not a frame sequence we can serve
 * (caller falls back to the legacy iframe, then the photo). Public: no auth.
 */
export const viewer360Api = {
  frames: (viewerUrl: string) => {
    // Two shapes reach us. A KYC-approved caller holds the REAL viewer URL and
    // sends it base64'd (`u=`). Everyone else holds `/api/v/360/<sealed>` — an
    // opaque token only the server can open — and sends the token itself
    // (`t=`). Re-encoding the token as `u=` would just hand back a 400, which
    // is what would have quietly killed the 360 tab for anonymous browsers.
    const q = viewerUrl.startsWith(MASKED_360_PREFIX)
      ? `t=${encodeURIComponent(viewerUrl.slice(MASKED_360_PREFIX.length))}`
      : `u=${encodeURIComponent(b64url(viewerUrl))}`
    return api<{ resolved: boolean; count: number; frames: string[] }>(
      `/v/frames?${q}`,
      { anonymous: true },
    )
  },
}

function b64url(s: string): string {
  const b64 = btoa(unescape(encodeURIComponent(s)))
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/** Aska+ concierge agent — the "Chad" thread coordinating an order's supplier. */
export interface AgentStep {
  key: string
  label: string
  status: 'done' | 'active' | 'pending' | 'blocked'
}
/** One message in the agent↔supplier transcript (admin cockpit only). */
export interface AgentMessageView {
  direction: 'outbound' | 'inbound'
  channel: string
  to: string | null
  from: string | null
  subject: string | null
  body: string
  draft: boolean
  model: string | null
  tokens: number
  at: string
}
/** The concierge move the agent proposed off a supplier reply (human confirms). */
export interface AgentProposalView {
  to: string | null
  applyable: boolean
  reason: string
  trackingNumber: string | null
  trackingUrl: string | null
  invoiceNumber: string | null
  needsHuman: boolean
  at: string
  appliedAt: string | null
  appliedBy: string | null
}
export interface AgentThreadView {
  id: string
  orderId: string
  agentName: string
  agentAvatar: string
  state: string
  steps: AgentStep[]
  extracted: {
    supplierConfirmed: boolean
    trackingNumber: string | null
    trackingUrl: string | null
    invoiceNumber: string | null
    supplierNote: string | null
  }
  supplierName: string | null
  /** Admin-only (buyers get these stripped). */
  supplierPhone?: string | null
  supplierEmail?: string | null
  channel: string
  dryRun: boolean
  escalationReason: string | null
  /** Admin cockpit only — the agent's proposed next concierge move + wa.me send link + transcript. */
  proposal?: AgentProposalView | null
  waLink?: string | null
  messages?: AgentMessageView[]
  updatedAt: string
}
export const agentApi = {
  threads: (orderId: number | string) => api<{ threads: AgentThreadView[] }>(`/agent/threads/${orderId}`),
  start: (orderId: number | string) =>
    api<{ started: number }>(`/agent/threads/${orderId}/start`, { method: 'POST' }),
  // Admin cockpit ops — drive one thread by hand while ASKA_CONCIERGE_LIVE is off.
  tick: (threadId: string) => api<AgentThreadView>(`/agent/threads/${threadId}/tick`, { method: 'POST' }),
  simulateReply: (threadId: string, body: { text?: string; intent?: string; trackingNumber?: string; invoiceNumber?: string }) =>
    api<AgentThreadView>(`/agent/threads/${threadId}/simulate-reply`, { method: 'POST', body }),
  applyProposal: (threadId: string) =>
    api<{ ok: true } & AgentThreadView>(`/agent/threads/${threadId}/proposal/apply`, { method: 'POST' }),
  dismissProposal: (threadId: string) =>
    api<{ ok: true } & AgentThreadView>(`/agent/threads/${threadId}/proposal/dismiss`, { method: 'POST' }),
  sendEmail: (threadId: string) =>
    api<{ ok: boolean; sent: boolean; provider: string } & AgentThreadView>(`/agent/threads/${threadId}/send/email`, { method: 'POST' }),
}

export const ordersApi = {
  list: () => api<{ orders: Order[] }>('/orders'),
  detail: (orderId: number) => api<Order>(`/orders/${orderId}`),
  invoices: () => api<{ orders: Order[] }>('/orders/invoices'),
  // Per-line rejection metadata + isPartial flag (Brief A — THE-BIG-PLAN §2.2).
  rejections: (orderId: number) => api<{
    rejections: Array<{ orderDetailId: number; reasonCode: number; reasonText: string | null; reasonLabel: string; rejectedAt: string | null }>;
    isPartial: boolean;
  }>(`/orders/${orderId}/rejections`),
}

/**
 * Supplier inbox API — Brief A. Mirrors Heera's /Admin/SupplierConfirmStones
 * + /Admin/SupplierConfirmLineDecisions surface. Suppliers (UserType=2)
 * and admins (UserType=1) can call. Pure buyers get 403.
 */
export interface SellerLine {
  orderId: number
  orderDate: string | null
  userId: number
  orderDetailId: number
  stockId: string
  carat: number
  amount: number
  status: string
}
export interface RejectReason {
  code: number
  label: string
  displayOrder: number
}
export interface LineDecision {
  orderDetailId: number
  status: 'approve' | 'reject'
  reasonCode?: number
  reasonText?: string
}
export interface LineDecisionOutcome {
  orderDetailId: number
  orderId: number
  status: 'confirmed' | 'rejected'
  reasonCode?: number
  reasonText?: string
  stockId?: string
  amount?: number
  allowed: boolean
}

export const sellerApi = {
  pending: (status: 'pending' | 'confirmed' | 'rejected' | 'all' = 'pending') =>
    api<{ lines: SellerLine[] }>(`/seller/pending?status=${status}`),
  rejectReasons: () => api<{ reasons: RejectReason[] }>('/seller/reject-reasons'),
  lineDecisions: (lines: LineDecision[]) =>
    api<{ success: boolean; updated: number; outcomes: LineDecisionOutcome[] }>(
      '/seller/line-decisions',
      { method: 'POST', body: { lines } }
    ),
  // Brief D — supplier inventory writes. Used by the chat-confirm
  // buttons (mark-unavailable / update-stock) and by the auto-prompt
  // after a per-line rejection.
  markUnavailable: (stockIds: string[], reason?: string) =>
    api<{ flagged: string[]; skipped: Array<{ stockId: string; reason: string }>; requested: number }>(
      '/seller/mark-unavailable',
      { method: 'POST', body: { stockIds, reason } }
    ),
  /** Update the supplier's PUBLIC contact (what verified buyers see). */
  updateContact: (updates: { phone?: string; email?: string; contactName?: string; returns?: { accepting: boolean; windowDays?: number | null; note?: string | null } }) =>
    api<{ ok: true; updated: string[] }>('/seller/contact', { method: 'POST', body: updates }),
  updateStock: (stones: Array<{ stockId: string; rate?: number; cut?: string; polish?: string; symmetry?: string; certNo?: string; lab?: string }>) =>
    api<{ updated: string[]; skipped: Array<{ stockId: string; reason: string }>; requested: number }>(
      '/seller/update-stock',
      { method: 'POST', body: { stones } }
    ),

  // Brief J — per-stone image / video upload. Two-step pattern:
  //   1. mediaUploadUrl({stockId, kind, contentType, ext}) -> signed PUT URL
  //   2. client PUTs raw bytes directly to that URL
  //   3. mediaConfirm({stockId, kind, url}) persists URL on the stone doc
  // mediaDelete removes the file + clears the field.
  mediaUploadUrl: (stockId: string, body: { kind: 'image' | 'video'; contentType: string; ext: string }) =>
    api<{ uploadUrl: string; downloadUrl: string; expiresAt: string; method: string; headers: Record<string, string>; objectPath: string }>(
      `/seller/stones/${encodeURIComponent(stockId)}/media/upload-url`,
      { method: 'POST', body }
    ),
  mediaConfirm: (stockId: string, body: { kind: 'image' | 'video'; url: string }) =>
    api<{ ok: boolean; media: { imageUrl: string | null; videoUrl: string | null } }>(
      `/seller/stones/${encodeURIComponent(stockId)}/media`,
      { method: 'POST', body }
    ),
  mediaDelete: (stockId: string, kind: 'image' | 'video') =>
    api<{ ok: boolean; deletedFromStorage: boolean }>(
      `/seller/stones/${encodeURIComponent(stockId)}/media?kind=${kind}`,
      { method: 'DELETE' }
    ),

  // R1 — supplier-scoped order management. A subscribed supplier confirms →
  // ships → delivers THEIR OWN storefront orders (only orders containing this
  // supplier's lines). Reuses the shared fulfillment state machine; the buyer
  // gets the same status emails as the admin board. Backend re-scopes on the
  // token, so a buyer / other-supplier gets 403. `setOrderStatus` surfaces the
  // backend's { code:'invalid_transition' | 'tracking_required' } via ApiError.body.
  orders: (params: { status?: string; limit?: number; offset?: number } = {}) => {
    const qs = new URLSearchParams()
    if (params.status) qs.set('status', params.status)
    if (params.limit != null) qs.set('limit', String(params.limit))
    if (params.offset != null) qs.set('offset', String(params.offset))
    const s = qs.toString()
    return api<{ orders: SellerOrderRow[]; total: number; limit: number; offset: number }>(
      `/seller/orders${s ? `?${s}` : ''}`)
  },
  orderDetail: (orderId: string) =>
    api<{ order: SellerOrderRow }>(`/seller/orders/${encodeURIComponent(orderId)}`),
  setOrderStatus: (orderId: string, body: {
    to: FulfillmentStatus
    carrier?: string | null
    trackingNumber?: string | null
    trackingUrl?: string | null
    note?: string | null
  }) => api<{ ok: true; orderId: string; fulfillment: Fulfillment }>(
    `/seller/orders/${encodeURIComponent(orderId)}/status`, { method: 'POST', body }),
}

/**
 * A row from GET /seller/orders — the supplier-scoped order shape. Same enriched
 * shape as the admin board (AdminOrderRow) but lines are already filtered to
 * THIS supplier's stones, and it carries the supplier's own subtotal.
 */
export interface SellerOrderRow extends AdminOrderRow {
  /** Subtotal of THIS supplier's lines on the order (not the whole-order total). */
  supplierSubtotal?: number | null
}

/** What the /signup wizard saves — see functions/src/me signup-profile. */
export interface SignupProfileBody {
  accountType: 'supplier' | 'buyer' | 'broker'
  contactName: string
  termsAccepted: true
  companyName?: string
  phone?: string
  /** Optional second number — the company/office line. */
  companyPhone?: string
  /** SMS/WhatsApp consent from the wizard checkbox (unchecked by default);
   *  the server stamps the timestamp and stores yes AND no. */
  messagingOptIn?: boolean
  website?: string
  /** Structured — server resolves countryId (Heera) from the ISO-2 country. */
  address?: {
    line1: string
    line2?: string
    city: string
    state?: string
    postalCode?: string
    country: string
    countryName?: string
  }
  hearAbout?: string
  /**
   * What the organization DOES — the wizard's capabilities step. Optional and
   * additive: the server infers it from `accountType` when absent (the old
   * rule), and `'both'` is the value that step exists to produce. It must
   * AGREE with `accountType` (sell ⇒ 'supplier'); the server refuses a
   * contradiction rather than silently picking one.
   */
  tradeRole?: 'buyer' | 'supplier' | 'both'
  brokerReferences?: Array<{ name: string; company?: string; email?: string; phone?: string; relationship?: string }>
  brokerAffiliation?: string
}

/* ───────────── SIGNUP PROFILE: SURVIVING A HOSTING-ONLY DEPLOY ─────────────
 *
 * Firebase hosting and functions are SEPARATE deploy targets and hosting is
 * routinely deployed alone (CLAUDE.md's deploy line names both; plenty of
 * pushes have shipped only one). So a new bundle can meet an old API, and
 * `POST /me/signup-profile` is the worst possible place for that to matter:
 * its schema is `.strict()`, so an unknown key is a **400 with nothing
 * written** — and the wizard calls it AFTER the Firebase Auth account already
 * exists. A user in that window has a real account and no profile: the exact
 * half-created strand the no-dead-ends rule forbids.
 *
 * `tradeRole` is the field that would have done it today: the client sends it,
 * the deployed API (api-00196-wuq / 8492346) has never heard of it.
 *
 * ── WHY DEGRADE-ON-REJECTION, and not a capability probe ──────────────────
 * The obvious alternative is to advertise the accepted fields on
 * `/api/health` (it is already the capability channel: feePercent, orgsLive,
 * seatPlan) and only send what the server admits to. It was rejected:
 *   • Health hydration is a fire-and-forget boot fetch (lib/askaServices.ts).
 *     A wizard that reaches the credentials step before it lands — a fast
 *     typist, a slow network, a Capacitor cold start — would drop `tradeRole`
 *     against a server that DOES accept it, silently losing the one fact the
 *     capabilities step exists to produce (`'both'`). Failing safe on the
 *     wrong axis: it makes the NEW-server case lossy to protect the old one.
 *   • It needs a functions deploy before the field can ever be sent, i.e. it
 *     solves deploy-order by requiring exactly the deploy order it can't
 *     assume.
 *   • It answers only for the fields we remembered to enumerate. This answers
 *     for whatever the server actually rejected.
 * Omitting the field until health advertises it has the same two problems and
 * also parks dead code in the wizard.
 *
 * So: send the truth, and if the server says "I don't know that key", drop
 * exactly that key and send again. One retry, no recursion. The new-server
 * path is unchanged and un-taxed (no extra request, no boot dependency); the
 * old-server path costs one extra round-trip and lands the same profile the
 * old client would have sent, with `tradeRole` inferred from `accountType`
 * server-side (the pre-existing rule, me/index.ts::resolveSignupTradeRole).
 *
 * ── THE TWO GUARDRAILS ────────────────────────────────────────────────────
 * 1. Only `unrecognized_keys` triggers it. The server ALSO 400s with a custom
 *    issue at path `['tradeRole']` when the value contradicts `accountType`
 *    ("selling makes this a supplier account"). Retrying without the field
 *    would turn a contradiction the server refused into a silently-accepted
 *    wrong answer. Matching on zod's structural code — never on the path —
 *    keeps the two apart.
 * 2. Only ADDITIVE keys are droppable. A stripped `termsAccepted` would save a
 *    profile with no consent stamp; a stripped `contactName` would 400 again
 *    anyway. If the server names anything outside this set, the original error
 *    is rethrown untouched.
 */
const DROPPABLE_SIGNUP_FIELDS: ReadonlyArray<keyof SignupProfileBody> = ['tradeRole']

/** Keys a strict-zod server said it did not recognise, from an ApiError body.
 *  Reads zod's structured issue first and falls back to its message text, so
 *  a server that only echoes `{error}` is still understood. Exported for the
 *  deploy-order test — it is the whole safety argument. */
export function unrecognizedKeysFrom(err: unknown): string[] {
  if (!(err instanceof ApiError) || err.status !== 400) return []
  const body = err.body as { issues?: unknown; error?: unknown } | null | undefined
  const keys = new Set<string>()
  if (Array.isArray(body?.issues)) {
    for (const raw of body.issues as Array<Record<string, unknown>>) {
      if (raw?.code !== 'unrecognized_keys') continue
      const named = Array.isArray(raw.keys) ? raw.keys : []
      for (const k of named) if (typeof k === 'string') keys.add(k)
      // Older/other zod shapes put the keys only in the message.
      if (!named.length && typeof raw.message === 'string') {
        for (const k of keysFromZodMessage(raw.message)) keys.add(k)
      }
    }
  }
  if (!keys.size && typeof err.message === 'string') {
    for (const k of keysFromZodMessage(err.message)) keys.add(k)
  }
  return [...keys]
}

/** `Unrecognized key(s) in object: 'tradeRole', 'x'` → ['tradeRole','x']. */
function keysFromZodMessage(message: string): string[] {
  if (!/unrecognized key/i.test(message)) return []
  return [...message.matchAll(/'([^']+)'/g)].map((m) => m[1])
}

/* ───────────────────────── Profile (multi-country contacts) ─────────────────
 *
 * The editable source of truth for everything signup collected: company name,
 * contact person, website, address, and the per-country phone book with a
 * WhatsApp designate. Maps to GET/POST /me/profile (functions/src/me/index.ts).
 */

/** One phone line under a country. */
export interface ProfilePhone {
  number: string
  /** +E164 when derivable, else null (read-only echo from the server). */
  e164?: string | null
  isWhatsApp: boolean
  isPrimary?: boolean
  label?: string | null
}
/** A country a supplier operates in, with its lines. */
export interface ProfileCountry {
  countryCode: string
  countryName?: string | null
  phones: ProfilePhone[]
}
export interface ProfileContacts {
  countries: ProfileCountry[]
}
/** The structured address the form edits — country is ISO-2. */
export interface ProfileAddress {
  line1: string
  line2?: string
  city: string
  state?: string
  postalCode?: string
  country: string
  countryName?: string | null
}
export interface Profile {
  accountType: 'supplier' | 'buyer' | 'broker' | null
  role: 'admin' | 'supplier' | 'buyer'
  companyName: string | null
  contactName: string | null
  website: string | null
  email: string | null
  address: ProfileAddress | null
  contacts: ProfileContacts
  returns: { accepting: boolean; windowDays?: number | null; note?: string | null } | null
  /** The PERSONAL number — how this individual reaches the DiaEx agent on
   *  WhatsApp (per person), distinct from the business contact numbers in
   *  `contacts` (how buyers reach the company). Read-only: OTP-attested,
   *  changed only through the verification flow. */
  phone?: string | null
  phoneVerified?: boolean
}
/** What POST /me/profile accepts. Every field optional — send only what changed;
 *  the server merges. `.strict()` server-side: an unknown key is a 400. */
export interface ProfileUpdate {
  companyName?: string | null
  contactName?: string | null
  website?: string | null
  address?: ProfileAddress | null
  contacts?: ProfileContacts
  returns?: { accepting: boolean; windowDays?: number | null; note?: string | null }
}

export const meApi = {
  get: () => api<{ user: UserClaims; terms: PaymentTerms | null }>('/me'),
  /** The full editable profile (contacts resolved from the stored block or a
   *  legacy single phone). */
  profile: () => api<{ profile: Profile }>('/me/profile'),
  /** Save profile changes. Returns the re-read profile. */
  updateProfile: (body: ProfileUpdate) =>
    api<{ ok: true; profile: Profile }>('/me/profile', { method: 'POST', body }),
  /**
   * Save the signup-wizard answers. Called right after account creation
   * (partial) and again on wizard finish (full) — server merges.
   *
   * Deploy-order safe in BOTH directions (see the block above): a new bundle
   * against the deployed strict-zod API drops the key that API rejected and
   * retries once, so a mid-wizard account can always finish. An old bundle
   * against a new API simply omits `tradeRole`, which the server infers.
   */
  signupProfile: async (body: SignupProfileBody) => {
    type Saved = { ok: boolean; accountType: string; phoneVerified: boolean; kycStatus: string }
    try {
      return await api<Saved>('/me/signup-profile', { method: 'POST', body })
    } catch (err) {
      const named = unrecognizedKeysFrom(err)
      if (!named.length) throw err
      // Every key the server named must be one we can safely drop AND one we
      // actually sent. Anything else and we do not understand this rejection.
      const droppable = DROPPABLE_SIGNUP_FIELDS as ReadonlyArray<string>
      if (!named.every((k) => droppable.includes(k) && k in body)) throw err
      const retry = { ...body } as Record<string, unknown>
      for (const k of named) delete retry[k]
      return await api<Saved>('/me/signup-profile', { method: 'POST', body: retry })
    }
  },
  /** Self-serve account deletion (#14, App Store requirement). Irreversible:
   *  deletes the profile tree, chat history, push tokens, KYC docs and the
   *  Firebase Auth user. Orders survive (financial records). The `confirm`
   *  literal is enforced server-side so a stray call can't nuke an account. */
  deleteAccount: () =>
    api<{ deleted: boolean }>('/me/delete-account', {
      method: 'POST',
      body: { confirm: 'DELETE' },
    }),
  /** Business-registration country (ISO-2) — feeds the payment-rails matrix
   *  (which payment methods the buyer's checkout renders). Saved from the KYC
   *  page's country selector; the Stripe webhook also self-heals it from the
   *  first completed checkout's shipping address. */
  setCountry: (country: string) =>
    api<{ ok: boolean; country: string }>('/me/country', { method: 'POST', body: { country } }),
}

/* ─────────────────────── Organizations — discovery + join ───────────────────
 *
 * The signup fork's "your team is already here" branch. Everything here is
 * behind the `/org` router, which 404s entirely while `ORGS_LIVE` is off — so
 * the CONTRACT of this block is that a dark backend is indistinguishable from
 * "no match", and the wizard simply never grows the step.
 */

/** One offer from `GET /org/discover`. The server answers with the MINIMUM: a
 *  name and the id needed to address a request at it. No member count, no
 *  verification state, no profile — see functions/src/org/discover.ts. */
export interface OrgMatch {
  orgId: string
  name: string
  /** Domain matches are strong; a typed name alone is never more than a guess. */
  basis: 'email_domain' | 'company_name'
  confidence: 'high' | 'low'
}

/** The caller's OWN join request, as they see it. Their row — leaks nothing. */
export interface MyJoinRequest {
  requestId: string | null
  orgId: string | null
  orgName: string | null
  status: 'pending' | 'approved' | 'declined' | 'cancelled' | 'expired' | null
  matchBasis: 'email_domain' | 'company_name' | null
  matchConfidence: 'high' | 'low' | null
  createdAt: string | null
  expiresAt: string | null
  decidedAt: string | null
  declineReason: string | null
}

export interface OrgDiscovery {
  matches: OrgMatch[]
  pendingRequest: MyJoinRequest | null
}

const NO_DISCOVERY: OrgDiscovery = { matches: [], pendingRequest: null }

/** Bound on the one call that sits in the signup critical path. A discovery
 *  that is slower than this is worth less than the two seconds it costs. */
const DISCOVER_TIMEOUT_MS = 2500

export const orgApi = {
  /**
   * "Is the company you just typed already on DiaEx?"
   *
   * SWALLOWS EVERY FAILURE to the empty answer — 404 (ORGS_LIVE off), 429,
   * 401, a timeout, a parse error, an offline device. This is the single call
   * in the wizard allowed to fail silently, and it is what lets the whole join
   * branch ship inert: with the flag off the step does not exist, zero pixels
   * and zero errors, and signup behaves exactly as it does today.
   *
   * The email is NOT a parameter the caller controls in any meaningful sense —
   * the server derives the domain from the verified Auth record and ignores a
   * `?email=` that disagrees. It is sent only for wire compatibility.
   */
  discover: async (company?: string | null, signal?: AbortSignal): Promise<OrgDiscovery> => {
    const ctl = new AbortController()
    const onAbort = () => ctl.abort()
    signal?.addEventListener('abort', onAbort, { once: true })
    const timer = setTimeout(() => ctl.abort(), DISCOVER_TIMEOUT_MS)
    try {
      const qs = company?.trim() ? `?company=${encodeURIComponent(company.trim())}` : ''
      const r = await api<Partial<OrgDiscovery>>(`/org/discover${qs}`, {
        signal: ctl.signal,
        // NEVER let a convenience call raise the session overlay. This fires
        // seconds after `createUser`, while the fresh token is still
        // propagating; a transient 401 here would drop a focus-trapping
        // "you've been signed out" screen on top of a half-finished signup —
        // the exact dead end this whole pass exists to stop shipping.
        skipSessionHandler: true,
      })
      return {
        matches: Array.isArray(r?.matches) ? r.matches.filter((m) => m && m.orgId && m.name) : [],
        pendingRequest: r?.pendingRequest ?? null,
      }
    } catch {
      return NO_DISCOVERY
    } finally {
      clearTimeout(timer)
      signal?.removeEventListener('abort', onAbort)
    }
  },

  /**
   * Ask an org to let you in. NEVER a join: the server re-derives the match
   * from the caller's own verified email and an owner/admin has to approve.
   * Errors are NOT swallowed — the receipt row renders "Try again" beside
   * "Continue without it", so the caller needs to know it failed.
   */
  requestJoin: (targetOrgId: string, company?: string | null) =>
    api<{ request: MyJoinRequest; notified: { sent: number; redirected: boolean } }>(
      '/org/join-requests',
      { method: 'POST', body: { targetOrgId, ...(company?.trim() ? { company: company.trim() } : {}) } },
    ),

  /** THE UNDO. One outstanding request per person, so without this an ask sent
   *  to the wrong company blocks asking the right one. */
  cancelJoinRequest: () =>
    api<{ cancelled: boolean }>('/org/join-requests/mine', { method: 'DELETE' }),

  /* ── the org itself: profile, roster, invites (step 11) ────────────────
   *
   * NONE of these swallow their errors, unlike `discover` above. Every one of
   * them backs a screen whose whole job is to say what happened — a 402 is the
   * paywall, a 403 is "you're a member, not an admin", a 409 is a conflict with
   * a different exit. Collapsing them to `null` is how a surface ends up with
   * one shrug for six different situations. The CALLER branches on
   * `orgErrorCode()` (lib/orgAccess.ts) and renders the matching way out.
   *
   * With `ORGS_LIVE` off the whole `/org` router 404s, which `orgErrorCode()`
   * reports as `not_found` and every screen renders as "this feature isn't on
   * here" — never as an error.
   */

  /** The caller's own org. A SUSPENDED member gets `{org:{restricted:true,
   *  seatStatus:'suspended'}, seatSuspended:true}` — an identity-only view, not
   *  a 403. Do not treat a missing `seats` block as zero seats; treat it as
   *  "this view doesn't carry seats" (see OrgFull.restricted). */
  get: (signal?: AbortSignal) =>
    api<{ org: OrgFull; seatSuspended?: boolean }>('/org', { signal }),

  /** Owner/admin only. `.strict()` server-side — sending a field that isn't in
   *  the allowlist is a 400 and NOTHING is written. */
  patch: (body: OrgPatchBody) =>
    api<{ org: OrgFull; changed: string[] }>('/org', { method: 'PATCH', body }),

  members: (signal?: AbortSignal) =>
    api<{ orgId: string; members: OrgMemberRow[] }>('/org/members', { signal }),

  /** Remove someone, or (target === your own uid) leave. `freedSeat` tells the
   *  UI whether the meter actually gave a segment back. */
  removeMember: (uid: string) =>
    api<{ removed: boolean; uid: string; freedSeat: boolean }>(
      `/org/members/${encodeURIComponent(uid)}`, { method: 'DELETE' },
    ),

  /** Owner-only in practice: an ADMIN gets a specific 403 naming the rule, and
   *  `orgRole:'owner'` gets a 400 naming `transfer-ownership`. Render the
   *  server's message; it is better than anything invented here. */
  setMemberRole: (uid: string, orgRole: 'admin' | 'member') =>
    api<{ updated: boolean; uid: string; orgRole: string }>(
      `/org/members/${encodeURIComponent(uid)}`, { method: 'PATCH', body: { orgRole } },
    ),

  transferOwnership: (uid: string) =>
    api<{ transferred: boolean; orgId: string; ownerUid: string }>(
      '/org/transfer-ownership', { method: 'POST', body: { uid } },
    ),

  /** Owner/admin only — a plain member 403s, so don't call it for one. */
  invites: (signal?: AbortSignal) =>
    api<{ orgId: string; invites: OrgInviteRow[] }>('/org/invites', { signal }),

  /**
   * RESERVES A SEAT. 402 `seat_limit_reached` carries the whole `seats` block,
   * which is why the paywall opens with no second round trip.
   */
  createInvite: (email: string, orgRole?: 'admin' | 'member') =>
    api<{ invite: OrgInviteRow; emailed: { ok: boolean; to: string; redirected: boolean } }>(
      '/org/invites', { method: 'POST', body: { email, ...(orgRole ? { orgRole } : {}) } },
    ),

  revokeInvite: (inviteId: string) =>
    api<{ revoked: boolean; inviteId: string }>(
      `/org/invites/${encodeURIComponent(inviteId)}/revoke`, { method: 'POST' },
    ),

  /** Spend the token. 409 = already used / revoked / expired / already in
   *  another org; 403 = the link was forwarded to the wrong person.
   *  `phone` is an OPTIONAL personal number captured for the joiner (best-effort,
   *  never blocks the join) so they're resolvable when they WhatsApp our agent. */
  acceptInvite: (token: string, phone?: string) =>
    api<OrgInviteAccepted>('/org/invites/accept', {
      method: 'POST',
      body: phone?.trim() ? { token, phone: phone.trim() } : { token },
    }),

  /**
   * The owner/admin's join queue (the other way a seat gets consumed).
   * `queueUnavailable:true` means the QUERY failed — the server refuses to let
   * a broken index look like "nobody has asked", and so must the screen.
   */
  joinRequests: (signal?: AbortSignal) =>
    api<{
      orgId: string
      requests: OrgJoinRequestRow[]
      queueUnavailable?: true
      seats: OrgSeats | null
    }>('/org/join-requests', { signal }),

  /** CONSUMES A SEAT, exactly like an invite — and 402s at the limit, which is
   *  the same paywall from the admin's side. */
  approveJoinRequest: (uid: string) =>
    api<{ approved: boolean }>(
      `/org/join-requests/${encodeURIComponent(uid)}/approve`, { method: 'POST' },
    ),

  declineJoinRequest: (uid: string, reason?: string) =>
    api<{ declined: boolean }>(
      `/org/join-requests/${encodeURIComponent(uid)}/decline`,
      { method: 'POST', body: reason?.trim() ? { reason: reason.trim() } : {} },
    ),
}

/* ── org wire types ────────────────────────────────────────────────────────
 * Mirrors functions/src/org/view.ts. Every field is optional-or-nullable on
 * purpose: the SUSPENDED projection genuinely omits `seats`, `kyc`, `billing`
 * and `ownerUid`, and a client that types them as required will read
 * `undefined.used` on the one screen a suspended person is allowed to see. */

export interface OrgSeats {
  used: number
  included: number
  paid: number
  limit: number
  pendingInvites: number
}

export interface OrgFull {
  id: string
  kind: 'company' | 'broker'
  tradeRole: 'buyer' | 'supplier' | 'both' | null
  capabilities?: { buy: boolean; sell: boolean }
  legalName: string
  displayName: string | null
  website?: string | null
  address?: Record<string, unknown> | null
  billingCountryCode?: string | null
  registrationNumbers?: Record<string, string | null>
  hearAbout?: string | null
  kyc?: {
    approved: boolean
    status: 'not_submitted' | 'submitted' | 'approved' | 'rejected'
    submittedAt: string | null
    reviewedAt: string | null
    rejectionReason: string | null
    documentCount: number
  }
  seats?: OrgSeats
  billing?: {
    subscriptionStatus: string
    quantity: number | null
    currentPeriodEnd: string | null
    source: string
  } | null
  ownerUid?: string | null
  createdAt?: string | null
  updatedAt?: string | null
  viewerOrgRole: 'owner' | 'admin' | 'member' | null
  /** Only on the suspended projection — the reason the view is small. */
  restricted?: true
  seatStatus?: 'suspended'
}

/**
 * The editable set, mirroring `OrgPatchSchema` EXACTLY. That schema is
 * `.strict()`: one extra key is a 400 and nothing is written at all — so this
 * type is not documentation, it is the thing that stops a save silently
 * failing because someone added a field to a form. `kycApproved`, `seats`,
 * `billing`, `ownerUid` and `orgRole` are absent on purpose.
 */
export interface OrgPatchBody {
  legalName?: string
  displayName?: string | null
  website?: string | null
  address?: {
    line1: string
    line2?: string
    city: string
    state?: string
    postalCode?: string
    /** ISO-2. `countryId` (the pricing tier) is derived server-side and is
     *  never accepted from a client. */
    country: string
    countryName?: string
  } | null
  registrationNumbers?: {
    duns?: string | null
    incorporation?: string | null
    sellersPermit?: string | null
    gst?: string | null
    vat?: string | null
    taxId?: string | null
  }
  tradeRole?: 'buyer' | 'supplier' | 'both'
  capabilities?: { buy?: boolean; sell?: boolean }
}

export interface OrgMemberRow {
  uid: string
  email: string | null
  displayName: string | null
  orgRole: 'owner' | 'admin' | 'member' | null
  status: 'active' | 'suspended' | string
  joinedAt: string | null
  invitedByUid: string | null
  seatCountsAgainstLimit: boolean
}

export interface OrgInviteRow {
  inviteId: string
  email: string | null
  orgRole: 'admin' | 'member' | null
  status: 'pending' | 'accepted' | 'revoked' | 'expired' | null
  createdAt: string | null
  expiresAt: string | null
}

export interface OrgInviteAccepted {
  joined: boolean
  alreadyMember: boolean
  orgId: string
  orgRole: 'owner' | 'admin' | 'member'
  orgName: string | null
  retiredOrgId: string | null
  pointersWritten: number
  /** True when a personal number was captured from this accept call. */
  personalPhoneCaptured?: boolean
  /** True when the joiner still has no resolvable personal number — the panel
   *  prompts for one so they can reach our agent on WhatsApp. */
  needsPersonalPhone?: boolean
}

export interface OrgJoinRequestRow {
  requestId?: string | null
  uid: string
  email: string | null
  displayName?: string | null
  status: string | null
  matchBasis?: 'email_domain' | 'company_name' | null
  matchConfidence?: 'high' | 'low' | null
  createdAt: string | null
  expiresAt?: string | null
}

/* ─────────────────────────── Seat billing (money) ──────────────────────────
 *
 * `canPurchaseSeats()` is imported here DELIBERATELY and enforced at the
 * transport layer, not only in the UI: `seatPlan.guard.test.ts` requires every
 * file touching these two paths to consult it, and a gate that lives only in a
 * component is one careless `<button>` away from being bypassed. Seats are
 * WEB-ONLY for v1 — a hosting deploy must never drop a purchase call into the
 * reviewed App Store build.
 */

export interface SeatBillingState {
  configured: boolean
  plan: {
    includedFree: number
    flatUpTo: number
    flatCents: number
    extraSeatCents: number
    currency: string
    interval: string
    live: boolean
  }
  seats: OrgSeats
  current: { cents: number; currency?: string } | number
  nextSeat: { cents: number; currency?: string } | number
  subscription: {
    status: string
    quantity: number | null
    currentPeriodEnd: string | null
    graceEndsAt: string | null
    source: string
    manageable: boolean
  }
  /** Present only on the inert answer. Render it verbatim. */
  code?: 'not_configured'
  message?: string
}

/** Thrown instead of calling a money route from a shell that may not sell. */
export class SeatPurchaseUnavailable extends Error {
  constructor() { super('Seats are managed on the web.') }
}

export const seatBillingApi = {
  /** Owner/admin only. Read-only and safe on every shell. */
  get: (signal?: AbortSignal) => api<SeatBillingState>('/billing/seats', { signal }),

  /**
   * OWNER only. `seats` is the TOTAL the org wants, free tier included — the
   * same number the customer read. Answers `{configured:false, message}` while
   * the rail is inert; that is a 200, not an error, and the caller renders the
   * message rather than spinning forever.
   */
  checkoutSession: async (seats: number): Promise<SeatBillingState & { url?: string }> => {
    if (!canPurchaseSeats()) throw new SeatPurchaseUnavailable()
    return api<SeatBillingState & { url?: string }>('/billing/seats/checkout-session', {
      method: 'POST', body: { seats },
    })
  },

  /** OWNER only. The Stripe-hosted manage page — where a seat COUNT change and
   *  a card update happen (a second Checkout Session would bill twice). */
  portal: async (): Promise<SeatBillingState & { url?: string }> => {
    if (!canPurchaseSeats()) throw new SeatPurchaseUnavailable()
    return api<SeatBillingState & { url?: string }>('/billing/seats/portal', { method: 'POST' })
  },
}

/* ─────────────────────────── Aska+ membership ─────────────────────────────── */

export interface AskaPlusState {
  /** false ⇒ {configured:false, message}: render the price, hide the join CTA. */
  configured: boolean
  plan: AskaPlusPlan
  membership: AskaPlusMembership
  code?: 'not_configured'
  message?: string
}

export interface AskaPlusInvoice {
  id: string
  number: string | null
  status: string | null
  amountCents: number | null
  currency: string | null
  createdIso: string | null
  hostedInvoiceUrl: string | null
  pdfUrl: string | null
}

/** The DashPass saving for a specific set of stones, computed SERVER-side. */
export interface AskaPlusUpsell {
  member: boolean
  live: boolean
  worthShowing: boolean
  priceCents?: number
  currency?: string
  memberFeePercent?: number
  feePercent?: number
  baseUsd?: number
  savingUsd?: number
  feeSavingUsd?: number
  qcDiscountUsd?: number
  shippingSavingUsd?: number
  /** FLAT model (FLAT_PRICING_LIVE): present when the server priced this
   *  pitch under the flat model — the client renders the flat copy ("skip
   *  the $15 fee · 2% protection") instead of the %-spread pitch. */
  flat?: boolean
  serviceFeeUsd?: number
  guaranteeValue?: number
  guaranteeMemberValue?: number
}

/** Thrown instead of calling a money route from a shell that may not sell. */
export class AskaPlusJoinUnavailable extends Error {
  constructor() { super('Aska+ is managed on the web.') }
}

/**
 * Aska+ membership — the PER-BUYER subscription. Read routes are safe on every
 * shell; the join/portal routes go through `canJoinAskaPlus()` (the native +
 * arm gate) exactly like seats. `cancel`/`reactivate` are portal-adjacent
 * shortcuts (cancel at period end) that still need the rail live.
 */
export const askaPlusApi = {
  get: (signal?: AbortSignal) => api<AskaPlusState>('/billing/aska-plus', { signal }),

  invoices: (signal?: AbortSignal) =>
    api<{ configured: boolean; invoices: AskaPlusInvoice[] }>('/billing/aska-plus/invoices', { signal }),

  /** The DashPass number for THIS cart/stone. Read-only, works while inert. */
  upsell: (stockIds: string[]) =>
    api<AskaPlusUpsell>('/billing/aska-plus/upsell', { method: 'POST', body: { stockIds } }),

  checkoutSession: async (): Promise<AskaPlusState & { url?: string }> => {
    if (!canJoinAskaPlus()) throw new AskaPlusJoinUnavailable()
    return api<AskaPlusState & { url?: string }>('/billing/aska-plus/checkout-session', { method: 'POST' })
  },

  portal: async (): Promise<AskaPlusState & { url?: string }> => {
    if (!canJoinAskaPlus()) throw new AskaPlusJoinUnavailable()
    return api<AskaPlusState & { url?: string }>('/billing/aska-plus/portal', { method: 'POST' })
  },

  cancel: async (): Promise<AskaPlusState> => {
    if (!canJoinAskaPlus()) throw new AskaPlusJoinUnavailable()
    return api<AskaPlusState>('/billing/aska-plus/cancel', { method: 'POST' })
  },

  reactivate: async (): Promise<AskaPlusState> => {
    if (!canJoinAskaPlus()) throw new AskaPlusJoinUnavailable()
    return api<AskaPlusState>('/billing/aska-plus/reactivate', { method: 'POST' })
  },
}

/** A favorited supplier — contact snapshot taken at save time. */
export interface FavoriteSupplier {
  supplierId: number
  name: string | null
  contactName: string | null
  phone: string | null
  email: string | null
  country: string | null
  savedAt: number
}

/**
 * Favorite suppliers (Tej 2026-08-03) — the buyer's contact book. Hearted
 * from the stone modal, listed on /favorites for one-tap prefilled outreach.
 */
export const favoritesApi = {
  list: () => api<{ suppliers: FavoriteSupplier[] }>('/me/favorite-suppliers'),
  toggle: (s: {
    supplierId: number
    name?: string | null
    contactName?: string | null
    phone?: string | null
    email?: string | null
    country?: string | null
  }) =>
    api<{ favorited: boolean }>('/me/favorite-suppliers/toggle', {
      method: 'POST',
      body: s,
    }),
}

/**
 * A favorited stone — the server persists ONLY the stockId identifier (no
 * price, no availability), so this list is deliberately thin. The client
 * re-fetches each stone's CURRENT data through the marketplace API
 * (searchApi.detail → applyMarketplace) on view: prices stay KYC-gated, and
 * a sold/delisted stone comes back 404 → rendered as "no longer available".
 */
export interface FavoriteStone {
  stockId: string
  savedAt: number
}

/**
 * Favorite stones — the buyer's saved-diamonds list. Bookmarked from the
 * stone detail, listed on /favorites alongside favorite suppliers. Mirrors
 * favoritesApi; stores only the identifier, never a price snapshot.
 */
export const favoriteStonesApi = {
  list: () => api<{ stones: FavoriteStone[] }>('/me/favorite-stones'),
  toggle: (stockId: string) =>
    api<{ favorited: boolean }>('/me/favorite-stones/toggle', {
      method: 'POST',
      body: { stockId },
    }),
}

/**
 * Historical supplier applications (the retired /sell/apply form — supplier
 * onboarding now happens in the signup wizard). Only the admin review list
 * survives, so the Applications tab can still show pre-cutover submissions.
 */
export type SupplierStockMethod = 'api' | 'excel' | 'erp_push'

export interface SupplierApplication {
  id: string
  status: string
  businessName: string | null
  website: string | null
  email: string | null
  contactName: string | null
  message: string | null
  stockMethod: SupplierStockMethod | null
  apiUrl: string | null
  authNotes: string | null
  sampleFilePath: string | null
  sampleFileName: string | null
  uid: string | null
  userId: number | null
  createdAt: string | null
}

export const supplierApi = {
  // Admin review list.
  adminList: () =>
    api<{ applications: SupplierApplication[] }>('/admin/supplier-applications'),
}

/**
 * Supplier data source — how a supplier will feed us inventory (API / Excel
 * sample / ERP push / later). Captured on the signup wizard's supplier
 * "connect your stock" step, editable from My Stock. Maps to
 * /me/supplier-data-source/* (authed; supplier accountType or role).
 */
export type SupplierDataMethod = 'docs' | 'api' | 'excel' | 'erp_push' | 'later'

/** Mirrors functions/src/me/supplierMapping.ts — the states an integration
 *  can actually be in. Nothing before `approved` may claim stock is arriving. */
export type IntegrationStatus =
  | 'not_started' | 'draft' | 'needs_input' | 'pending_review' | 'approved' | 'live' | 'rejected'

export interface IntegrationStatusCopy {
  title: string
  blurb: string
  tone: 'idle' | 'wait' | 'act' | 'good' | 'bad'
}

/** The declarative request shape. GET-only was the bug: every real supplier
 *  feed we ingest is a POST (see functions/src/me/supplierMapping.ts). */
export interface ConnectionSpec {
  url: string
  httpMethod: 'GET' | 'POST'
  bodyMode: 'none' | 'json' | 'form'
  bodyJson?: string
  formFields?: Array<{ name: string; value: string }>
  queryParams?: Array<{ name: string; value: string }>
  authMode: 'none' | 'bearer' | 'header' | 'query'
  apiKeyHeader?: string
  apiKeyParam?: string
  recordsPath?: string
}

export interface MappedField {
  canonical: string
  label: string
  source: string | null
  origin: 'auto' | 'answer' | 'none'
  required: boolean
  samples: string[]
}

export interface MappingQuestion {
  id: string
  sourceKey: string
  prompt: string
  samples: string[]
  options: Array<{ value: string; label: string }>
  answer: string | null
}

/** An AI pre-fill hint for an unplaced column — a SUGGESTION the supplier
 *  confirms, never an applied mapping (functions/src/me/supplierMappingAI.ts). */
export interface AiColumnSuggestion {
  sourceKey: string
  canonical: string
  confidence: number
  question: string
}

export interface MappingProposal {
  ok: boolean
  recordCount: number
  fields: MappedField[]
  questions: MappingQuestion[]
  unmatched: Array<{ key: string; samples: string[] }>
  missingRequired: string[]
  media: { imageSamples: string[]; videoSamples: string[]; hasMedia: boolean }
  /** AI suggestions for the columns the deterministic matcher couldn't place. */
  aiSuggestions?: AiColumnSuggestion[]
}

export interface SupplierApiTestResult {
  ok: boolean
  at: string
  url: string
  /** The request we actually made, in one line ("POST · JSON body · header apikey"). */
  request?: string
  httpStatus: number | null
  contentType: string | null
  recordCount: number | null
  sampleFields: string[]
  recognized: string[]
  note: string
  error: string | null
  mapping?: MappingProposal | null
}

export interface SheetVerification {
  at: string
  objectPath: string
  fileName: string
  rowCount: number | null
  ok: boolean
  matched: Array<{ canonical: string; header: string }>
  missingRequired: string[]
  missingRecommended: string[]
  mapping?: MappingProposal | null
}

export interface MediaCheckItem {
  url: string
  ok: boolean
  httpStatus: number | null
  contentType: string | null
  hostKnown: boolean
  host: string | null
  note: string
}

export interface MediaCheckResult {
  at: string
  results: MediaCheckItem[]
  unknownHosts: string[]
}

export interface SupplierMapping {
  at: string
  answers: Record<string, string>
  notes: string | null
  mediaPattern: string | null
  confirmed: boolean
  fields?: Array<{ canonical: string; source: string | null; samples: string[] }>
}

export interface SupplierDataSource {
  method: SupplierDataMethod
  status?: IntegrationStatus
  updatedAt?: string
  submittedAt?: string
  api: { url: string; keyHeader: string | null; hasKey: boolean; keyMasked: string | null } | null
  connection?: ConnectionSpec
  docs?: { url: string | null; file: { objectPath: string; fileName: string } | null }
  sheet?: { objectPath: string; fileName: string; uploadedAt: string }
  sheetVerification?: SheetVerification
  erp?: { docsUrl: string | null; instructions?: { objectPath: string; fileName: string } }
  lastTest?: SupplierApiTestResult
  mapping?: SupplierMapping
  mediaCheck?: MediaCheckResult
  notes?: string | null
  review?: { decision: string; message: string | null; by: string | null; at: string | null } | null
}

/** The 7-day connect window, served as NUMBERS. Nothing gates on it. */
export interface ConnectDeadline {
  dueAt: string | null
  daysLeft: number | null
  overdue: boolean
  windowDays: number
  applies: boolean
}

export interface SupplierDataSourceState {
  dataSource: SupplierDataSource | null
  status: IntegrationStatus
  statusCopy: IntegrationStatusCopy
  deadline: ConnectDeadline
  org: { orgId: string; orgRole: string; canEdit: boolean } | null
}

export const supplierDataApi = {
  get: () => api<SupplierDataSourceState>('/me/supplier-data-source'),

  save: (body: {
    method: SupplierDataMethod
    apiUrl?: string
    apiKey?: string
    apiKeyHeader?: string
    connection?: ConnectionSpec
    docsUrl?: string
    docsFile?: { objectPath: string; fileName: string }
    sheet?: { objectPath: string; fileName: string }
    erpDocsUrl?: string
    erpInstructions?: { objectPath: string; fileName: string }
    notes?: string
  }) =>
    api<{ ok: true; dataSource: SupplierDataSource; status: IntegrationStatus }>('/me/supplier-data-source', {
      method: 'POST', body,
    }),

  /** Hand the ball to our team. Separate from save on purpose — a half-filled
   *  record in a human queue teaches the reviewer to ignore the queue. */
  submit: () =>
    api<{ ok: true; status: IntegrationStatus; statusCopy: IntegrationStatusCopy; dataSource: SupplierDataSource }>(
      '/me/supplier-data-source/submit', { method: 'POST' },
    ),

  /** Live-probe a feed — the full request shape, and what we could read back. */
  testApi: (body: {
    url?: string
    apiKey?: string
    apiKeyHeader?: string
    connection?: ConnectionSpec
    answers?: Record<string, string>
  }) =>
    api<{ result: SupplierApiTestResult }>('/me/supplier-data-source/test-api', {
      method: 'POST', body,
    }),

  /** Confirm the field map (and the answers to our questions) before anything
   *  prices a real diamond off it. */
  saveMapping: (body: {
    answers: Record<string, string>
    notes?: string
    mediaPattern?: string
    confirmed?: boolean
  }) =>
    api<{ ok: true; mapping: SupplierMapping }>('/me/supplier-data-source/mapping', { method: 'POST', body }),

  /** Do the pictures actually load? */
  mediaCheck: (body: { urls?: string[]; pattern?: string; sampleStockId?: string }) =>
    api<{ result: MediaCheckResult }>('/me/supplier-data-source/media-check', { method: 'POST', body }),

  /** 2-step signed-URL upload (mirror of kycApi.uploadFile). */
  uploadFile: async (
    kind: 'sample_sheet' | 'erp_instructions' | 'api_docs',
    file: File,
  ): Promise<{ objectPath: string; fileName: string }> => {
    const contentType = file.type || 'application/octet-stream'
    const ext = (file.name.split('.').pop() || 'bin').toLowerCase()
    const { uploadUrl, objectPath } = await api<{ uploadUrl: string; objectPath: string; expiresAt: number }>(
      '/me/supplier-data-source/upload-url',
      { method: 'POST', body: { kind, contentType, ext } },
    )
    const put = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': contentType }, body: file })
    if (!put.ok) {
      const err = new Error(`Upload failed (HTTP ${put.status})`) as Error & { status?: number }
      err.status = put.status
      throw err
    }
    return { objectPath, fileName: file.name }
  },

  /** Server-side column check of an uploaded sample sheet. */
  verifySheet: (body: { objectPath: string; fileName: string }) =>
    api<{ result: SheetVerification }>('/me/supplier-data-source/verify-sheet', {
      method: 'POST', body,
    }),
}

/**
 * PAYOUT RAIL — supplier Stripe Connect onboarding ("connect your bank so DiaEx
 * can pay you"). Both endpoints are supplier/admin gated + INERT until a Stripe
 * key is configured: onboard returns { configured:false } (not an error) when
 * unarmed, so the UI shows a clean "not available yet" state.
 */
export interface ConnectStatus {
  merchantId: string
  configured: boolean
  armed: boolean
  stripeConnectId: string | null
  chargesEnabled: boolean
  payoutsEnabled: boolean
  detailsSubmitted: boolean
}
export const connectApi = {
  status: () => api<ConnectStatus>('/seller/connect/status'),
  onboard: () =>
    api<{ configured: boolean; url?: string; stripeConnectId?: string; code?: string; message?: string }>(
      '/seller/connect/onboard', { method: 'POST' }),
}

/**
 * Saved payment methods — Stripe SetupIntent on platform, mirror of Auth.Net
 * CIM on Heera. Same response shape across the surfaces so the management
 * UI is identical.
 */
export interface SavedPaymentMethod {
  paymentMethodId: string
  provider: 'stripe' | 'authnet'
  brand: string
  last4: string
  expMonth: number
  expYear: number
  isDefault: boolean
  createdAt: string
}

export const paymentMethodsApi = {
  list: () => api<{ profiles: SavedPaymentMethod[] }>('/me/payment-methods'),
  add: (body: { successUrl?: string; cancelUrl?: string } = {}) =>
    api<{
      provider: 'stripe'
      mode: 'sim' | 'live'
      customerId: string
      clientSecret: string
      setupIntentId?: string
      publishableKey: string | null
    }>('/me/payment-methods/add', { method: 'POST', body }),
  // Sim-mode-only — never called when a live Stripe key is set.
  simConfirm: (body: { brand?: string; last4?: string; expMonth?: number; expYear?: number; paymentMethodId?: string }) =>
    api<{ ok: true; paymentMethodId: string } & Omit<SavedPaymentMethod, 'paymentMethodId'>>(
      '/me/payment-methods/sim-confirm', { method: 'POST', body }),
  setDefault: (paymentMethodId: string) =>
    api<{ ok: true }>('/me/payment-methods/default', { method: 'POST', body: { paymentMethodId } }),
  remove: (paymentMethodId: string) =>
    api<{ ok: true }>('/me/payment-methods/remove', { method: 'POST', body: { paymentMethodId } }),
}

/**
 * Credit module — per-order cycle.
 *
 * Each order has its own due date = orderDate + paymentTerms.days. The buyer
 * page surfaces every unpaid order with its individual clock; "Pay" logs a
 * pending PaymentAllocation that admin reviews on /admin.
 *
 * Server: functions/src/credit/index.ts (mirror of Heera's CreditService).
 */
export interface CreditOrderRow {
  orderId: number
  orderDate: string
  dueDate: string
  amount: number
  paidAmount: number
  balAmount: number
  daysToDue: number
  isOverdue: boolean
}

export interface CreditPendingPayment {
  paymentId: string
  orderId: number
  amount: number
  method: string
  reference: string | null
  notes: string | null
  ts: string
}

/** Buyer financing standing + terms (Revenue Stream 3, #64). */
export interface CreditFinancing {
  live: boolean
  approved: boolean
  apr: number
  graceDays: number
  interestAccrued: number
  nextInterestDate: string | null
}

export interface CreditState {
  hasCreditAccount: boolean
  creditLimit?: number
  usedCredit?: number
  availableCredit?: number
  termsDays?: number
  hasOverdue?: boolean
  orders?: CreditOrderRow[]
  pendingPayments?: CreditPendingPayment[]
  financing?: CreditFinancing
}

export interface FinancingTermsResponse {
  live: boolean
  /** Buyer signed up for Aska Credit (part of Aska Services) — prerequisite for approval. */
  enrolled: boolean
  approved: boolean
  apr: number
  graceDays: number
  defaultApr: number
  defaultGraceDays: number
  creditLimit: number
  requestPending: boolean
}

export const creditApi = {
  state: () => api<CreditState>('/me/credit'),
  logPayment: (body: {
    orderId: number
    amount: number
    method: 'BankTransfer' | 'Card' | 'CashAtOffice' | 'Other'
    reference?: string | null
    notes?: string | null
  }) => api<{ ok: true; paymentId: string }>('/me/credit/payment', { method: 'POST', body }),
  requestLimit: (body: { requestedLimit: number; reason?: string | null }) =>
    api<{ ok: true; requestId: string }>('/me/credit/limit', { method: 'POST', body }),
  // Revenue Stream 3 — financing terms + enable-request.
  financing: () => api<FinancingTermsResponse>('/me/credit/financing'),
  requestFinancing: (body: { reason?: string | null; requestedLimit?: number | null }) =>
    api<{ ok: true; requestId: string }>('/me/credit/financing/request', { method: 'POST', body }),
}

/**
 * Admin credit review surface — pending-payment confirm/reject and
 * limit-request approve/deny. Gated server-side by requireRole('admin').
 */
export interface AdminPendingPayment {
  paymentId: string
  userId: number
  userEmail: string | null
  userName: string | null
  orderId: number
  amount: number
  method: string
  reference: string | null
  notes: string | null
  ts: string
}

export interface AdminLimitRequest {
  requestId: string
  userId: number
  userEmail: string | null
  userName: string | null
  currentLimit: number
  requestedLimit: number
  reason: string | null
  status: string
  ts: string
}

export const adminCreditApi = {
  pendingPayments: () =>
    api<{ payments: AdminPendingPayment[] }>('/admin/credit/pending-payments'),
  decidePayment: (paymentId: string, body: { decision: 'confirmed' | 'rejected'; notes?: string | null }) =>
    api<{ ok: true; decision: 'confirmed' | 'rejected'; orderId?: number; newBalance?: number }>(
      `/admin/credit/payment/${encodeURIComponent(paymentId)}/decide`, { method: 'POST', body }),
  limitRequests: (status: 'pending' | 'approved' | 'denied' = 'pending') =>
    api<{ requests: AdminLimitRequest[] }>(`/admin/credit/limit-requests?status=${status}`),
  decideLimit: (requestId: string, body: {
    decision: 'approved' | 'denied'
    notes?: string | null
    approvedLimit?: number
  }) => api<{ ok: true; decision: 'approved' | 'denied'; approvedLimit?: number }>(
    `/admin/credit/limit/${encodeURIComponent(requestId)}/decide`, { method: 'POST', body }),
}

/**
 * Admin financing surface (Revenue Stream 3, #64) — approve credit standing +
 * set APR / grace / limit, review financing requests, and see accrued-interest
 * revenue. Gated server-side by requireRole('admin').
 */
export interface AdminFinancingRequest {
  requestId: string
  userId: number
  userEmail: string | null
  userName: string | null
  reason: string | null
  requestedLimit: number | null
  status: string
  ts: string
}

export interface AdminInterestRow {
  ledgerId: string
  orderId: string | null
  buyerUserId: string | number | null
  amount: number
  days: number
  accruedAt: string | null
  settled: boolean
}

export interface AdminInterestSummary {
  merchant: string
  totalInterest: number
  settledInterest: number
  unsettledInterest: number
  count: number
  rows: AdminInterestRow[]
}

export const adminFinancingApi = {
  requests: (status: 'pending' | 'approved' | 'denied' = 'pending') =>
    api<{ requests: AdminFinancingRequest[] }>(`/admin/credit/financing-requests?status=${status}`),
  set: (body: {
    userId: number
    approved: boolean
    apr?: number
    graceDays?: number
    creditLimit?: number
    termsDays?: number
  }) => api<{ ok: true; userId: number }>('/admin/credit/financing/set', { method: 'POST', body }),
  interest: () => api<AdminInterestSummary>('/admin/credit/interest'),
}

export interface KycStatusResponse {
  status: 'approved' | 'submitted' | 'rejected' | 'not_submitted'
  kycApprove: number
  accountType?: 'supplier' | 'buyer' | 'broker' | null
  documents: Array<{ fileName: string; docType: string; sizeBytes: number; uploadedAt: string; objectPath?: string }>
  /** Business-registration country (ISO-2) — feeds the payment-rails matrix. */
  country?: string | null
  message: string
}

/**
 * KYC submission API. Backed by /api/me/kyc/* on Functions today (mirror
 * of Heera's /mobile/api/me/kyc/*). Same shape on both surfaces so the
 * platform UI is identical to mobile. Endpoint hits 404 until the Functions
 * deploy with the Heera-side parity ships — UI degrades to email fallback.
 */
export const kycApi = {
  status: () => api<KycStatusResponse>('/me/kyc/status'),
  // 3-step signed-URL upload (replaces the old multipart POST):
  //   1. POST /me/kyc/upload-url → a private signed PUT URL under kyc/{uid}/
  //   2. PUT the raw bytes straight to storage (no auth header on a signed URL)
  //   3. POST /me/kyc/submit with the doc manifest → flips status to 'submitted'
  // Signature is unchanged so Kyc.tsx keeps its one-file-per-tile flow.
  upload: async (docType: string, file: File): Promise<{ ok: boolean; docType: string; fileName: string; message: string }> => {
    const contentType = file.type || 'application/octet-stream'
    const ext = (file.name.split('.').pop() || 'bin').toLowerCase()

    const { uploadUrl, objectPath } = await api<{ uploadUrl: string; objectPath: string; expiresAt: number }>(
      '/me/kyc/upload-url',
      { method: 'POST', body: { docType, contentType, ext } },
    )

    const put = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': contentType }, body: file })
    if (!put.ok) {
      const err = new Error(`Upload failed (HTTP ${put.status})`) as Error & { status?: number }
      err.status = put.status
      throw err
    }

    // Each submit replaces the server-side manifest, so carry forward the
    // buyer's previously submitted docs (those that still have an objectPath)
    // — uploading a second file must not drop the first.
    let prior: Array<{ docType: string; objectPath: string; fileName: string; sizeBytes?: number }> = []
    try {
      const status = await kycApi.status()
      prior = (status.documents as Array<Record<string, unknown>>)
        .filter((d) => typeof d.objectPath === 'string' && d.docType !== docType)
        .map((d) => ({
          docType: String(d.docType),
          objectPath: String(d.objectPath),
          fileName: String(d.fileName ?? ''),
          sizeBytes: typeof d.sizeBytes === 'number' ? d.sizeBytes : undefined,
        }))
    } catch { /* first submission — nothing to carry forward */ }

    await api<{ ok: boolean; status: string }>('/me/kyc/submit', {
      method: 'POST',
      body: { documents: [...prior, { docType, objectPath, fileName: file.name, sizeBytes: file.size }] },
    })
    return { ok: true, docType, fileName: file.name, message: 'Uploaded — our team will review and email you.' }
  },

  // Wizard variant — steps 1–2 only (mint URL + PUT bytes), NO submit. The
  // signup wizard uploads files as they're picked and submits the whole
  // manifest once at the end, so a half-finished wizard never flips status.
  uploadFile: async (docType: string, file: File): Promise<{ docType: string; objectPath: string; fileName: string; sizeBytes: number }> => {
    const contentType = file.type || 'application/octet-stream'
    const ext = (file.name.split('.').pop() || 'bin').toLowerCase()
    const { uploadUrl, objectPath } = await api<{ uploadUrl: string; objectPath: string; expiresAt: number }>(
      '/me/kyc/upload-url',
      { method: 'POST', body: { docType, contentType, ext } },
    )
    const put = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': contentType }, body: file })
    if (!put.ok) {
      const err = new Error(`Upload failed (HTTP ${put.status})`) as Error & { status?: number }
      err.status = put.status
      throw err
    }
    return { docType, objectPath, fileName: file.name, sizeBytes: file.size }
  },

  /** Submit the final document manifest (flips kycStatus → 'submitted'). */
  submit: (documents: Array<{ docType: string; objectPath: string; fileName: string; sizeBytes?: number }>) =>
    api<{ ok: boolean; status: string }>('/me/kyc/submit', { method: 'POST', body: { documents } }),
}

/**
 * Checkout flow — maps to /api/checkout/* on the functions backend.
 *
 *   stripeSession POST /checkout/stripe-session — Hosted Checkout: returns a redirect URL
 *   paymentToken  POST /checkout/payment-token  — Embedded Elements: returns clientSecret
 *
 * The concierge flow (conciergeApi.place → stripeSession) is the only buyer path
 * that reaches Stripe; the old cart-checkout (/checkout/place, /checkout/summary)
 * has been removed with the storefront.
 */
/** Bank-transfer details returned when an order is placed with payMethod 'bank_transfer'. */
export interface WireDetails {
  placeholder: boolean
  region: string
  beneficiary: string | null
  bankName: string | null
  accountNumber: string | null
  routingOrIfsc: string | null
  iban: string | null
  swiftBic: string | null
  bankAddress: string | null
  currency: string
  note: string | null
}

/** Which payment rails this buyer's checkout should render (server-decided by country). */
export interface PaymentOptions {
  region: 'IN' | 'US' | 'EU' | 'ROW'
  country: string | null
  countryKnown: boolean
  card: boolean
  bankTransfer: boolean
  cardNote: string | null
}

export const checkoutApi = {
  stripeSession: (body: { orderId: number; successUrl?: string; cancelUrl?: string }) =>
    api<{ provider: 'stripe'; mode: 'hosted'; url: string; sessionId: string }>(
      '/checkout/stripe-session', { method: 'POST', body }),
  // Region-decided rail plan (GET /checkout/payment-options). The UI renders
  // only the rails this returns; the server re-enforces at place time anyway.
  paymentOptions: () => api<PaymentOptions>('/checkout/payment-options'),
  paymentToken: (body: { orderId: number; method: 'stripe-card' | 'plaid-ach' }) =>
    api<{
      provider: string
      mode: 'sim' | 'live' | 'hosted'
      clientSecret?: string | null
      publishableKey?: string | null
      amount?: number
      currency?: string
    }>('/checkout/payment-token', { method: 'POST', body }),
}

/**
 * DiaEx Concierge Services checkout. A buyer who doesn't want to phone the
 * supplier / do their own QC pays DiaEx a commission to coordinate + QC + ship a
 * stone.
 *
 *   place({stockId, shippingAddress}) POST /checkout/concierge/place — creates a
 *     pending_payment order with channel='concierge' + needsPayment:true. The
 *     caller then opens a Stripe Checkout Session (reuse checkoutApi.stripeSession)
 *     and redirects. The sale/commission is only recorded AFTER Stripe confirms
 *     the charge (webhook post-payment loop).
 *
 * Error codes surfaced via ApiError.body.code:
 *   already_sold(400)     — stone is no longer available
 *   self_purchase(403)    — a supplier can't concierge-buy their own stone
 *   kyc_required(403)     — buyer must complete KYC first
 *   stone_not_found(404)  — no such stone
 *   no_price(400)         — stone has no resolvable price
 */
/** The flat-model order breakdown (order.pricing) — server-computed at place
 *  time and snapshotted on the order; the client only ever displays it. */
export interface FlatOrderPricing {
  model: 'flat'
  base: number
  serviceFeeUsd: number
  serviceFeeWaived: 'member' | 'free_source' | null
  freeSourceUsed: boolean
  guaranteeSelected: boolean
  guaranteeFeeUsd: number
  guaranteeKind: 'percent' | 'flat'
  guaranteeRate: number
  cardSurchargeUsd: number
  member: boolean
  total: number
}

export const conciergeApi = {
  // payMethod 'diaex_credit' finances the order on Aska Credit (Revenue Stream 3):
  // no Stripe hop, the amount is added to the buyer's outstanding balance and the
  // response comes back { financed:true, needsPayment:false }. Server-gated on the
  // CREDIT_FINANCING_LIVE flag + an enrolled+approved standing + available limit
  // (402 {code} otherwise). Default (absent / 'card') = pay DiaEx now via Stripe.
  // payMethod 'bank_transfer' (region-gated via checkoutApi.paymentOptions):
  // no Stripe hop either — the order holds at pending_payment with the stones
  // reserved, and the response carries { bankTransfer:true, amountDue, wire }.
  // `acceptPrice` is the JIT reprice handshake: a first attempt on a stone the
  // supplier has repriced comes back 409 `stone_price_changed` carrying
  // `priceNow`, and the retry echoes that exact number back here. A number and
  // not a boolean on purpose — "charge me whatever it is now" is the surprise
  // the check exists to prevent (functions/src/checkout/index.ts).
  // `guarantee` — opt-in return protection (FLAT model only; ignored by the
  // % model). The rate is server config; this is only the buyer's choice.
  place: (body: { stockId: string; shippingAddress?: string; payMethod?: 'card' | 'diaex_credit' | 'bank_transfer'; promoCode?: string; acceptPrice?: number; guarantee?: boolean }) =>
    api<{
      orderId: number
      total: number
      channel: 'concierge'
      qcRoute: QcRoute
      needsPayment: boolean
      financed?: boolean
      financing?: { apr: number; graceDays: number }
      ledgerIds?: string[]
      bankTransfer?: boolean
      amountDue?: number
      shipping?: number
      wire?: WireDetails
      /** FLAT-model breakdown snapshotted on the order (server-computed). */
      pricing?: FlatOrderPricing
    }>('/checkout/concierge/place', { method: 'POST', body }),

  // Place ONE concierge order for the WHOLE cart (Tej 2026-07-27). Reads the
  // server cart, validates each stone, and returns a single order to pay for via
  // checkoutApi.stripeSession. `skipped` lists any cart stones that couldn't be
  // included (already sold / no price / etc.) so the caller can surface them.
  // `acceptTotal` is the cart-level version of the same handshake: anything
  // that moved (a line the supplier sold, a line they repriced) comes back 409
  // `cart_changed` with the resulting base total, and the retry echoes it.
  placeCart: (body: { payMethod?: 'card' | 'bank_transfer'; promoCode?: string; acceptTotal?: number; guarantee?: boolean } = {}) =>
    api<{
      orderId: number
      total: number
      channel: 'concierge'
      qcRoute: QcRoute
      needsPayment: boolean
      /** true = QA $0 order, committed with nothing charged. */
      free?: boolean
      lineCount: number
      skipped: Array<{ stockId: string; reason: string }>
      bankTransfer?: boolean
      amountDue?: number
      shipping?: number
      wire?: WireDetails
      /** FLAT-model breakdown snapshotted on the order (server-computed). */
      pricing?: FlatOrderPricing
    }>('/checkout/concierge/place-cart', { method: 'POST', body }),
}

/**
 * Sourcing / buy-side limit orders (docs/SOURCING-ORDERS-SPEC.md). INERT until
 * SOURCING_ORDERS_LIVE — every route answers `{ configured:false }` while the
 * flag is off, so the UI branches on `configured` and never shows a button that
 * errors. KYC (not membership) is the gate; a non-member places fine (show the
 * DashPass upsell, never a paywall). `acceptTotal` is the confirm-time reprice
 * handshake — a number, not a boolean, so a buyer is never charged an unseen total.
 */
export const sourcingApi = {
  config: () =>
    api<{ configured: boolean; fees: Record<string, number | string>; maxOpenPerMember: number }>(
      '/sourcing/config'),

  listOrders: () =>
    api<{ configured: boolean; orders?: SourcingOrderView[]; code?: string }>('/sourcing/orders'),

  place: (body: {
    spec: SearchFilters
    limitBasis: SourcingLimitBasis
    limitValue: number
    certifiedOnly?: boolean
    quantity?: number
    origin?: 'ticket' | 'ai_search'
  }) =>
    api<{ configured: boolean; order?: SourcingOrderView; member?: boolean; code?: string; error?: string }>(
      '/sourcing/orders', { method: 'POST', body }),

  confirm: (id: string, body: { payMethod: 'ach' | 'wire' | 'card'; guaranteeSelected?: boolean; acceptTotal?: number }) =>
    api<{
      configured: boolean
      ok?: boolean
      orderId?: number
      total?: number
      rail?: string
      sourcing?: unknown
    }>(`/sourcing/orders/${encodeURIComponent(id)}/confirm`, { method: 'POST', body }),

  cancel: (id: string) =>
    api<{ configured: boolean; ok?: boolean; status?: string }>(
      `/sourcing/orders/${encodeURIComponent(id)}/cancel`, { method: 'POST' }),

  patch: (id: string, body: { limitValue?: number; expiry?: { mode: 'gtc' | 'date'; at: string | null } }) =>
    api<{ configured: boolean; order?: SourcingOrderView | null }>(
      `/sourcing/orders/${encodeURIComponent(id)}`, { method: 'PATCH', body }),
}

/**
 * Agentic sourcing — the want-list / RFQ surface (docs/ASKA-PLUS-AGENTIC-SOURCING.md).
 * The client half of "Start sourcing": create a want-list seeded with the tray's
 * stones, then submit it to kick the RFQ fan-out (both inert / {configured:false}
 * until SOURCING_RFQ_LIVE arms — the caller branches on `configured`/`rfqConfigured`).
 */
export interface WantListView {
  id: string
  buyerUid: string
  orgId: string | null
  status: 'draft' | 'sourcing' | 'closed' | 'cancelled'
  wantIds: string[]
  createdAt: string
  updatedAt: string
}

/** One seed want in a "Start sourcing" create — a `specific` want (source THIS
 *  stone) or a `spec` want (a limit-order spec). */
export interface WantSeed {
  spec: Record<string, unknown>
  limitBasis: SourcingLimitBasis
  limitValue: number
  wantKind?: 'specific' | 'spec'
  specificStockId?: string | null
  sourceMode?: 'on_network' | 'rfq' | 'both'
}

/** The buyer-safe want view (BUILT server-side — no supplier identity). */
export interface BuyerWantView {
  id: string
  wantListId: string | null
  wantKind: 'specific' | 'spec'
  specificStockId: string | null
  sourceMode: 'on_network' | 'rfq' | 'both'
  spec: Record<string, unknown>
  limitBasis: SourcingLimitBasis
  limitValue: number
  status: string
  quotes: { stockId: string | null; quotedBase: number | null; quotedPerCarat: number | null; available: boolean; aboveListed?: boolean; shippingUsd?: number | null; at: string }[]
  rfq: { status: string; round: number; quoteCount: number } | null
  /** The off-platform settlement view (docs/…§13) — null until a quote is accepted. */
  settlement: BuyerSettlementView | null
  /** Per-supplier lanes (BUILT — never a merchantId). */
  lanes?: { lane: 'outreach' | 'waiting' | 'reminded' | 'quoted' | 'declined'; at: string }[]
  createdAt: string
  updatedAt: string
}

/** The buyer-safe settlement view (BUILT server-side — no merchantId / thread). */
export interface BuyerSettlementView {
  mode: 'attestation' | 'escrow'
  stage: 'accepted' | 'supplier_confirmed' | 'payment_instructions' | 'payment_claimed' | 'shipped' | 'delivered' | 'dispute' | 'cancelled'
  paymentInstructions: string | null
  buyerPaidAt: string | null
  shipments: { stockId: string; carrier: string | null; trackingRef: string | null; shipped: boolean }[]
  awaitingBuyerPayment: boolean
  disputeReason: string | null
  updatedAt: string
}

export const wantListApi = {
  list: () =>
    api<{ configured: boolean; rfqConfigured?: boolean; wantLists?: WantListView[]; code?: string }>(
      '/sourcing/want-lists'),

  create: (wants: WantSeed[]) =>
    api<{ configured: boolean; rfqConfigured?: boolean; wantList?: WantListView; code?: string; error?: string }>(
      '/sourcing/want-lists', { method: 'POST', body: { wants } }),

  submit: (id: string) =>
    api<{ configured: boolean; rfqConfigured?: boolean; status?: string; wants?: { wantId: string; ran: boolean }[]; code?: string }>(
      `/sourcing/want-lists/${encodeURIComponent(id)}/submit`, { method: 'POST' }),

  quotes: (wantId: string) =>
    api<{ configured: boolean; rfqConfigured?: boolean; want?: BuyerWantView; code?: string }>(
      `/sourcing/wants/${encodeURIComponent(wantId)}/quotes`),

  /** Accept a quote in ATTESTATION mode (docs/…§13) — opens the off-platform
   *  settlement. NO Stripe; money moves buyer→supplier directly. */
  acceptAttestation: (wantId: string, stockId: string) =>
    api<{ configured: boolean; rfqConfigured?: boolean; ok?: boolean; wantId?: string; stage?: string; code?: string; error?: string }>(
      `/sourcing/wants/${encodeURIComponent(wantId)}/accept-attestation`, { method: 'POST', body: { stockId } }),

  /** Buyer attests "I've paid the supplier" (docs/…§13) — advances the settlement
   *  to payment_claimed; the recon then asks the supplier to confirm receipt. */
  markPaid: (wantId: string) =>
    api<{ configured: boolean; rfqConfigured?: boolean; ok?: boolean; settlement?: BuyerSettlementView; code?: string; error?: string }>(
      `/sourcing/orders/${encodeURIComponent(wantId)}/mark-paid`, { method: 'POST' }),
}

export interface ChatMessageHistory {
  role: 'user' | 'assistant'
  text: string
}

export interface ChatAction {
  label: string
  route: string
}

/**
 * Structured supplier confirm. Returned when the agent prepares a supplier
 * write (mark sold / update stock / update public contact) via its tools.
 * The UI renders [Yes][Skip] and, on Yes, calls the matching sellerApi
 * endpoint — which re-checks the token role + ownership server-side.
 */
export interface ChatConfirm {
  kind: 'mark_unavailable' | 'update_stock' | 'update_contact'
  message: string
  payload:
    | { kind: 'mark_unavailable'; stockIds: string[] }
    | {
        kind: 'update_stock'
        stockId: string
        updates: { price?: number; cut?: string; color?: string; clarity?: string; carat?: number }
      }
    | { kind: 'update_contact'; updates: { phone?: string; email?: string; contactName?: string } }
}

/** One line of the agent's visible "thinking" trail (tool executions). */
export interface ChatStep {
  label: string
  detail?: string
}

export interface ChatResponse {
  response: string
  results?: Diamond[]
  stockIds?: string[]
  actions?: ChatAction[]
  confirm?: ChatConfirm
  /** Light agentic trail — what Gemma searched/checked, with counts. */
  steps?: ChatStep[]
  /** Total catalog matches for the last search (cards show the top slice). */
  totalMatches?: number
  /** The exact filters the last search ran — echo back as `lastFilters` on
   *  the next send so follow-ups refine instead of starting over. */
  appliedFilters?: SearchFilters
}

/**
 * Admin-only endpoints (analytics, chat-history viewer, KB management).
 * Server gates these behind requireRole('admin') — anonymous + buyer
 * roles get 401/403. The web pages render an empty state if the
 * request 401s so non-admins typing the URL just see "Sign in as admin."
 */
export interface KeywordsResponse {
  range: string
  sampledEvents: number
  shapes: { term: string; count: number }[]
  colors: { term: string; count: number }[]
  clarities: { term: string; count: number }[]
  queries: { term: string; count: number }[]
}

export interface FunnelResponse {
  range: string
  sampledEvents: number
  funnel: { stage: string; users: number }[]
}

/** KPI header numbers for the admin console (GET /admin/summary). */
export interface AdminSummary {
  stones: number
  users: number
  enquiries: number
  merchantCount: number
}

/** One reference an individual-broker signup supplied for verification. */
export interface BrokerReference {
  name: string
  company?: string | null
  email?: string | null
  phone?: string | null
  relationship?: string | null
}

/** A user row from GET /admin/users (first 200, no filter). */
export interface AdminUser {
  uid: string
  userId?: number
  email?: string | null
  companyName?: string | null
  contactPerson?: string | null
  contactName?: string | null
  displayName?: string | null
  role?: string | null
  userType?: number | null
  kycApproved?: boolean
  // Signup-wizard fields (2026-08) — what the KYC reviewer decides on.
  accountType?: 'supplier' | 'buyer' | 'broker' | null
  kycStatus?: string | null
  // Timestamps used to float actionable KYC to the top of the People list.
  kycSubmittedAt?: string | null
  updatedAt?: string | null
  kycDocuments?: Array<{ docType: string; objectPath: string; fileName: string; sizeBytes?: number; uploadedAt?: string }>
  phone?: string | null
  phoneVerified?: boolean
  website?: string | null
  address?: string | null
  hearAbout?: string | null
  brokerReferences?: BrokerReference[]
  brokerAffiliation?: string | null
  /** ── Org pointers (step 8). Denormalized hints off the user doc; the
   *  MEMBERSHIP row is the authoritative record server-side. Present so the
   *  console can group people by company and jump to the org review panel. */
  orgId?: string | null
  orgRole?: 'owner' | 'admin' | 'member' | null
  orgSeatStatus?: string | null
  /** Pointed-at org's kind, route-joined. 'company' member ⇒ KYC is decided
   *  per COMPANY (Companies lens); anything else ⇒ per person, here. */
  orgKind?: string | null
  /**
   * Supplier stock-connect choice from the wizard / My Stock card.
   *
   * MASKED. `GET /admin/users` used to return the user document verbatim,
   * `api.key` (a supplier's live stock-feed credential) included; step 8
   * replaced it with an allowlist projection, so the console now sees
   * `hasKey`/`keyMasked` exactly like the supplier's own `/me` view does.
   */
  supplierDataSource?: {
    method?: 'api' | 'excel' | 'erp_push' | 'later'
    updatedAt?: string | null
    api?: { url?: string | null; keyHeader?: string | null; hasKey?: boolean; keyMasked?: string | null } | null
    sheet?: { objectPath: string; fileName: string; uploadedAt?: string | null } | null
    sheetVerification?: SheetVerification | null
    erp?: { docsUrl?: string | null; instructions?: { objectPath: string; fileName: string } | null } | null
    lastTest?: SupplierApiTestResult | null
  } | null
  [k: string]: unknown
}

/* ─── Org console (step 8) ─────────────────────────────────────────────────
 * Every shape here is what the SERVER projects, not what the org document
 * holds: no supplier API key, no Stripe customer/subscription ids, no invite
 * token hashes. If a field you want is missing, add it to the projection in
 * `functions/src/admin/orgAdminView.ts` — never widen it back to a spread. */

export interface AdminOrgSeats {
  used: number
  included: number
  paid: number
  limit: number
  pendingInvites: number
}

export interface AdminOrgBilling {
  subscriptionStatus: string
  quantity: number | null
  currentPeriodEnd: string | null
  source: string
  hasStripeCustomer: boolean
  hasSubscription: boolean
}

/**
 * WHAT A DECISION WILL ACTUALLY DO on this deployment. Render it. With
 * `ORGS_LIVE` off nothing reads an org; with `ORG_KYC_LIVE` off the decision is
 * recorded but no member inherits it. A green "Approved" badge over either is
 * the console lying about the gate.
 */
export interface AdminOrgGate {
  orgsLive: boolean
  orgKycLive: boolean
  inheritanceActive: boolean
  notice: string | null
}

export interface AdminOrgRow {
  id: string
  kind: string
  tradeRole: string | null
  legalName: string
  displayName: string | null
  countryCode: string | null
  kycApproved: boolean
  kycStatus: 'not_submitted' | 'submitted' | 'approved' | 'rejected'
  kycSubmittedAt: string | null
  kycReviewedAt: string | null
  documentCount: number
  seats: AdminOrgSeats
  billing: AdminOrgBilling
  ownerUid: string | null
  legacyUserId: number | null
  merchantId: string | null
  hasSupplierDataSource: boolean
  /** Org markup mode: verified members browse at raw × (1 + percent/100) with
   *  Aska's contact instead of the supplier's. `percent: null` = the server
   *  default; the write response's `effectivePercent` says the real number. */
  markup: { enabled: boolean; percent: number | null } | null
  /** Owner's account no longer exists (tombstoned). Hidden by default in the
   *  queue; rendered with a grey badge when shown. */
  orphaned: boolean
  createdAt: string | null
  updatedAt: string | null
}

export interface AdminOrgMarkupResult {
  ok: boolean
  orgId: string
  markup: { enabled: boolean; percent: number | null }
  effectivePercent: number | null
  /** FALSE ⇒ recorded but INERT (ORG_MARKUP_LIVE off) — say so in the UI. */
  live: boolean
  /** TRUE ⇒ tombstoned org (owner deleted) — the decision applies to nobody. */
  orphaned: boolean
  membersInvalidated: number
  propagationSlaSeconds: number
}

export interface AdminOrgMember {
  uid: string
  email: string | null
  displayName: string | null
  orgRole: 'owner' | 'admin' | 'member' | null
  status: string
  suspendedReason: string | null
  suspendedAt: string | null
  joinedAt: string | null
  invitedByUid: string | null
  seatCountsAgainstLimit: boolean
  isOwner: boolean
  /** Seat documents: identity ('id'|'passport') + 'employment_proof'. */
  documents: Array<{ docType: string; fileName: string; objectPath: string }>
  hasIdentityDoc: boolean
  hasEmploymentProof: boolean
  occupiesSeat: boolean
}

export interface AdminOrgInvite {
  inviteId: string
  email: string | null
  orgRole: string | null
  status: string
  createdByUid: string | null
  createdAt: string | null
  expiresAt: string | null
  expired: boolean
}

export interface AdminOrgDevice {
  uid: string
  activeCount: number
  limit: number | null
  exempt: boolean
  devices: Array<{
    deviceId: string
    label: string | null
    platform: string | null
    native: boolean
    status: string
    firstSeenAt: string | null
    lastSeenAt: string | null
  }>
}

export interface AdminOrgDetail extends AdminOrgRow {
  website: string | null
  address: unknown
  billingCountryCode: string | null
  registrationNumbers: Record<string, string>
  hearAbout: string | null
  kycRejectionReason: string | null
  kycReviewedBy: string | null
  kycResubmittedAt: string | null
  kycDocuments: Array<{ docType: string; objectPath: string; fileName: string; sizeBytes: number | null; uploadedAt: string | null }>
  supplierContact: Record<string, string | null> | null
  supplierDataSource: AdminUser['supplierDataSource']
  creditFinancing: Record<string, unknown> | null
  terms: Record<string, unknown> | null
  members: AdminOrgMember[]
  invites: AdminOrgInvite[]
  devices: AdminOrgDevice[]
  membersTruncated: boolean
  gate: AdminOrgGate
  createdFrom: string | null
}

export interface AdminOrgListResponse {
  orgs: AdminOrgRow[]
  total: number
  limit: number
  offset: number
  sort: string
  counts: Record<string, number>
  /** Tombstoned orgs hidden from this response (renders "Show orphaned (N)"). */
  orphanedCount: number
  truncated: boolean
  gate: AdminOrgGate
}

export interface AdminOrgDetailResponse {
  org: AdminOrgDetail
  seatsStored: AdminOrgSeats
  memberCount: number
  activeMemberCount: number
}

export interface AdminOrgKycResult {
  ok: boolean
  orgId: string
  kycApproved: boolean
  kycStatus: string
  membersInvalidated: number
  propagationSlaSeconds: number
}

export interface AdminSeatGrantResult {
  ok: boolean
  orgId: string
  seats: AdminOrgSeats
  delta: number
  belowUsage: boolean
  warning: string | null
  membersInvalidated: number
  propagationSlaSeconds: number
}

/** An enquiry row from GET /enquiries (admin: who contacted whom). */
/** GET /admin/orders row — the v0 read-only Orders tab (mirror of
 *  functions/src/admin/orgAdminView.ts::AdminOrderListRow). */
export interface AdminOrderListRow {
  id: string
  orderDate: string | null
  /** Verbatim doc value — render via lib/orderStatus.ts, never a bare map. */
  status: string | null
  conciergeStage: string | null
  channel: string | null
  payMethod: string | null
  total: number | null
  balance: number | null
  buyer: { uid: string | null; userId: number | null; email: string | null; name: string | null }
  stones: Array<{ stockId: string | null; carat: number | null; amount: number | null }>
  pieces: number
}

export interface AdminEnquiry {
  id: string
  buyerUserId: string | number | null
  stockId: string | null
  tagNo: string | null
  supplierName: string | null
  channel: 'call' | 'whatsapp' | 'email' | null
  createdAt: string | null
}

export const marketingApi = {
  armed: () => api<MarketingConfig>('/admin/marketing/armed'),
  arm: (armed: boolean, allowlistPhone?: string | null) =>
    api<MarketingConfig>('/admin/marketing/arm', { method: 'POST', body: { armed, ...(allowlistPhone !== undefined ? { allowlistPhone } : {}) } }),
  draft: (body: { prompt: string; channel: MarketingChannel; category: MarketingCategory; userIds?: string[] }) =>
    api<MarketingDraft>('/admin/marketing/draft', { method: 'POST', body }),
  update: (body: { campaignId: string; subject?: string; bodyHtml?: string; whatsappText?: string }) =>
    api<{ ok: boolean; campaignId: string }>('/admin/marketing/update', { method: 'POST', body }),
  previewAudience: (body: { category: MarketingCategory; userIds?: string[] }) =>
    api<MarketingAudiencePreview>('/admin/marketing/audience/preview', { method: 'POST', body }),
  send: (campaignId: string) =>
    api<MarketingSendResult>('/admin/marketing/send', { method: 'POST', body: { campaignId } }),
  campaigns: () => api<{ campaigns: MarketingCampaignSummary[] }>('/admin/marketing/campaigns'),
}

// ─── Supplier Insights & Demand Nudge ───────────────────────────────────────
// Ranked demand per supplier, plus an AI-drafted email that reuses the SAME
// _meta/marketing arm/allowlist safety (unarmed ⇒ every send is redirected to
// the allowlist and prefixed [TEST]).

export interface SupplierInsight {
  supplierId: string
  name: string | null
  email: string | null
  hasContact: boolean
  subscribed: false
  interestEvents: number
  distinctInterestedBuyers: number
  bySource: {
    filter_by_supplier: number
  }
  enquiries: number
  conciergeOrders: number
  conciergeGMV: number
  conciergeCommissionPaid: number
  projectedDirectSales: number
  opportunity: number
  demandScore: number
}

export interface SupplierInsightsResponse {
  period: string
  generatedAt: string
  thinData: boolean
  constants: {
    conversionRate: number
    defaultAov: number
    conciergeFeePercent: number
  }
  suppliers: SupplierInsight[]
}

/** The real numbers used to draft a supplier's conversion email. */
export interface SupplierDemandSnapshot {
  period: string
  interestEvents: number
  distinctInterestedBuyers: number
  enquiries: number
  conciergeOrders: number
  conciergeGMV: number
  conciergeCommissionPaid: number
  projectedDirectSales: number
  opportunity: number
}

/** A drafted supplier-conversion campaign from POST /admin/supplier-insights/draft. */
export interface SupplierDraft {
  campaignId: string
  subject: string
  bodyHtml: string
  supplierEmail: string | null
  supplierName: string | null
  demandSnapshot: SupplierDemandSnapshot
  aiGenerated: boolean
}

export interface SupplierSendResult {
  ok: boolean
  testMode: boolean
  recipients: number
  sentCount: number
  failedCount: number
  results: MarketingRecipientResult[]
  audienceEmail: string | null
}

/**
 * The v0 admin console API — exactly what the three tabs consume
 * (Users & KYC · Orders · Marketing, stats folded into Marketing).
 * The dead clients for the ~16 removed tabs (stones, ledger, fulfillment
 * board, concierge ops, feeds, kb, chat-history, stock-connect, merchants,
 * supplier-insights, sourcing comps) were deleted 2026-09-20 — their backend
 * routers died in the v0 trim, so every one of them pointed at a 404.
 */
export const adminApi = {
  summary: () => api<AdminSummary>('/admin/summary'),
  users: () => api<{ users: AdminUser[]; truncated?: boolean }>('/admin/users'),
  /**
   * The ORGANIZATION console (step 8).
   *
   * `kyc` is the ONLY surface that can approve a company — and approving a
   * company reveals prices, supplier contact and refStockId to every ACTIVE
   * member of it, including people who submitted nothing themselves. Rejecting
   * one denies every active member, overriding their own approvals, with no
   * per-person way back out. Render `gate.notice` and `membersInvalidated`;
   * never report a decision the flags make inert as a plain success.
   */
  orgs: {
    list: (params: {
      q?: string; kycStatus?: string; kind?: string; tradeRole?: string
      needsReview?: boolean; includeOrphaned?: boolean
      sort?: string; limit?: number; offset?: number
    } = {}) => {
      const qs = new URLSearchParams()
      if (params.q) qs.set('q', params.q)
      if (params.kycStatus) qs.set('kycStatus', params.kycStatus)
      if (params.kind) qs.set('kind', params.kind)
      if (params.tradeRole) qs.set('tradeRole', params.tradeRole)
      if (params.needsReview) qs.set('needsReview', '1')
      if (params.includeOrphaned) qs.set('orphaned', '1')
      if (params.sort) qs.set('sort', params.sort)
      if (params.limit != null) qs.set('limit', String(params.limit))
      if (params.offset != null) qs.set('offset', String(params.offset))
      const s = qs.toString()
      return api<AdminOrgListResponse>(`/admin/orgs${s ? `?${s}` : ''}`)
    },
    detail: (orgId: string) =>
      api<AdminOrgDetailResponse>(`/admin/orgs/${encodeURIComponent(orgId)}`),
    kyc: (orgId: string, approve: boolean, reason?: string) =>
      api<AdminOrgKycResult>(`/admin/orgs/${encodeURIComponent(orgId)}/kyc`, {
        method: 'POST', body: approve ? { approve } : { approve, reason },
      }),
    /** `paid` is the TOTAL granted seats, NOT an increment — the route has no
     *  idempotency key, so a double-submitted form must grant once. */
    grantSeats: (orgId: string, paid: number, note?: string) =>
      api<AdminSeatGrantResult>(`/admin/orgs/${encodeURIComponent(orgId)}/seats/grant`, {
        method: 'POST', body: note ? { paid, note } : { paid },
      }),
    /** Org markup mode. Omit `percent` to use the server default; render
     *  `live:false` as "recorded but not live", never as a plain success. */
    markup: (orgId: string, enabled: boolean, percent?: number) =>
      api<AdminOrgMarkupResult>(`/admin/orgs/${encodeURIComponent(orgId)}/markup`, {
        method: 'POST', body: percent != null ? { enabled, percent } : { enabled },
      }),
    /** Remove a member from a company (keeps their personal KYC — leaving is
     *  not a rejection). Owner refused: transfer or delete-from-Aska instead. */
    removeMember: (orgId: string, uid: string) =>
      api<{ removed: true; uid: string; freedSeat: boolean }>(
        `/admin/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(uid)}`,
        { method: 'DELETE' }),
  },
  /** Delete an account from Aska entirely (the shared self-service purge;
   *  orders retained). Same org guards; admins + self refused. */
  deleteUser: (uid: string) =>
    api<{ deleted: true; uid: string; orgDeleted: boolean }>(
      `/admin/users/${encodeURIComponent(uid)}/delete`,
      { method: 'POST', body: { confirm: 'DELETE' } }),
  /** The v0 Orders tab, read-only: the newest 300 orders as BUILT rows
   *  (no Stripe ids, no promo internals, no concierge agent state). */
  orders: () => api<{ orders: AdminOrderListRow[]; truncated: boolean }>('/admin/orders'),
  /** The Orders tab's Enquiries lens — who contacted which supplier about
   *  which stones. In a contact-only directory this IS the deal flow. */
  enquiries: () => api<{ items: AdminEnquiry[] }>('/enquiries'),
  setRole: (userId: string, role: 'buyer' | 'supplier' | 'admin') =>
    api<{ ok: boolean; role: string; firebaseUid: string; tokenRefreshRequired: boolean }>(
      '/admin/set-role', { method: 'POST', body: { userId, role } }),
  /** Set (or verify) a business's WhatsApp number — writes it as the supplier's
   *  designate AND marks it admin-verified. Omit `number` to just verify the
   *  current designate. */
  setSupplierWhatsapp: (userId: string, number?: string) =>
    api<{ ok: true; number: string; source: 'admin'; set: boolean }>(
      '/admin/whatsapp/verify', { method: 'POST', body: number ? { userId, number } : { userId } }),
  /** Stats strip inside the Marketing tab (Stats tab folded 2026-09-20). */
  analytics: {
    keywords: (range: string) =>
      api<KeywordsResponse>(`/admin/analytics/keywords?range=${encodeURIComponent(range)}`),
    funnel: (range: string) =>
      api<FunnelResponse>(`/admin/analytics/funnel?range=${encodeURIComponent(range)}`),
  },
  /**
   * Approve / reject a person's KYC.
   *
   * `kycApproved` is only what was WRITTEN. `effectiveVerified` is what the
   * GATE will answer — they differ when an org-level rejection (or a suspended
   * seat) overrides the approval, and this used to be invisible: the console
   * flashed "KYC approved" while the buyer stayed locked. Render `warning`
   * whenever `effectiveVerified` is false on an approve.
   */
  kyc: (userId: string, approve: boolean, reason?: string) =>
    api<{
      ok: boolean
      kycApproved: boolean
      effectiveVerified?: boolean
      blockedBy?: 'org_rejected' | 'seat_suspended' | null
      orgStillVerified?: boolean
      orgStillRejected?: boolean
      warning?: string | null
      reasonCode?: string
    }>('/admin/kyc', { method: 'POST', body: { userId, approve, reason } }),
  // Short-lived signed READ URL for a kyc/** document (reviewer opens uploads).
  kycDocUrl: (objectPath: string) =>
    api<{ url: string; expiresAt: number }>(
      '/admin/kyc/doc-url', { method: 'POST', body: { objectPath } }),
  marketing: marketingApi,
}

// ─── Agent Workboard — the admin "Agents" tab ────────────────────────────────

// ─── AI-drafted marketing (R36) ──────────────────────────────────────────────

export type MarketingChannel = 'email' | 'whatsapp' | 'both'
export type MarketingCategory = 'usa' | 'worldwide' | 'suppliers' | 'selected-users'

/** A stone the AI selected for a campaign — real image/price/link. */
export interface MarketingStonePick {
  uniqueId: string
  stockId: string | null
  shape: string | null
  carat: number
  color: string | null
  clarity: string | null
  priceUSD: number
  imageUrl: string | null
  url: string
}

/** A drafted campaign returned from POST /admin/marketing/draft. */
export interface MarketingDraft {
  campaignId: string
  prompt: string
  channel: MarketingChannel
  category: MarketingCategory
  audienceUserIds: string[]
  subject: string
  bodyHtml: string
  whatsappText: string
  stonePicks: string[]
  stonePickDetails: MarketingStonePick[]
  status: 'draft' | 'approved' | 'sent'
  aiGenerated: boolean
}

export interface MarketingConfig {
  armed: boolean
  allowlistEmails: string[]
  allowlistPhone: string | null
}

export interface MarketingAudiencePreview {
  count: number
  withEmail: number
  withPhone: number
  sample: Array<{ name: string | null; email: string | null; phone: string | null }>
}

export interface MarketingRecipientResult {
  userId: number | string | null
  email: string | null
  phone: string | null
  emailStatus?: 'sent' | 'failed' | 'skipped'
  emailError?: string | null
  whatsappStatus?: 'sent' | 'failed' | 'skipped'
  whatsappError?: string | null
}

export interface MarketingSendResult {
  ok: boolean
  campaignId: string
  testMode: boolean
  audienceResolvedCount: number
  recipients: number
  sentCount: number
  failedCount: number
  results: MarketingRecipientResult[]
}

export interface MarketingCampaignSummary {
  campaignId: string
  prompt: string | null
  channel: MarketingChannel | null
  category: MarketingCategory | null
  subject: string | null
  status: 'draft' | 'approved' | 'sent'
  testMode: boolean | null
  sentCount: number
  failedCount: number
  audienceResolvedCount: number | null
  stonePickCount: number
  createdBy: string | null
  createdAt: string | null
  sentAt: string | null
}


export const chatApi = {
  send: async (params: {
    userText: string
    conversationHistory?: ChatMessageHistory[]
    /** appliedFilters from the last search reply — lets "only VS1" refine
     *  the previous search server-side instead of starting over. */
    lastFilters?: SearchFilters
    systemContext?: string
    isAuthenticated?: boolean
    /** Caller role — selects which prompt persona + tool surface the agent
     * gets. The server only trusts the token's res.locals.role for DATA
     * gating and for authenticated callers ignores this body role entirely
     * (harmless if spoofed — a buyer can't trigger a supplier WRITE because
     * /seller/* re-checks the token). */
    role?: 'buyer' | 'supplier' | 'admin'
  }): Promise<ChatResponse> => {
    try {
      // NOT anonymous: /chat is mounted with optionalAuth, so attaching the
      // Firebase token (when signed in) is what lets the assistant be
      // role-aware — buyer vs supplier vs admin prompts + supplier intents.
      // Anon callers simply send no token and get the public experience.
      // Server never trusts the body `isAuthenticated` for data gating.
      return await api<ChatResponse>('/chat', {
        method: 'POST', body: params,
      })
    } catch (err) {
      // Fall back to the deterministic local stub ONLY when the backend is
      // genuinely absent: network/DNS/CORS (TypeError) or 404 (function not
      // mounted). A real server response — 401, 429, 5xx — must SURFACE so
      // the chat UI shows its "couldn't reach the assistant" error instead
      // of silently answering with canned replies + bundled sample stones
      // presented as live inventory. (Same rule as searchApi.query.)
      if (
        err instanceof TypeError ||
        (err instanceof ApiError && err.status === 404)
      ) {
        return localStubReply(params.userText)
      }
      throw err
    }
  },
}

/**
 * Persisted multi-session chat history for signed-in users. Backs the
 * sessions sidebar in Chat.tsx — the "recent chats" drawer the mobile
 * app has. Anonymous users get a localStorage-only mirror (Chat.tsx),
 * so these calls only fire when a token is present. Sessions are
 * soft-archived on delete (server keeps message rows for analytics).
 *
 * Timestamps come back as Firestore serverTimestamp values, which
 * serialize as { _seconds, _nanoseconds } over JSON — Chat.tsx
 * normalizes them via `tsToMillis`.
 */
export interface ChatSessionMeta {
  sessionId: string
  title?: string | null
  isArchived?: boolean
  lastRole?: string | null
  updatedAt?: unknown
}

export interface StoredChatMessage {
  messageId: string
  role: 'user' | 'assistant' | 'system'
  text: string
  resultSetJson?: string | null
  ts?: unknown
}

export const chatHistoryApi = {
  sessions: () => api<{ sessions: ChatSessionMeta[] }>('/chat-history/sessions'),
  session: (sessionId: string) =>
    api<{ sessionId: string; messages: StoredChatMessage[] }>(
      `/chat-history/session/${encodeURIComponent(sessionId)}`),
  append: (body: {
    sessionId: string
    role: 'user' | 'assistant' | 'system'
    text: string
    resultSetJson?: string
    title?: string
  }) => api<{ ok: true }>('/chat-history/append', { method: 'POST', body }),
  archive: (sessionId: string) =>
    api<{ archived: true }>(
      `/chat-history/session/${encodeURIComponent(sessionId)}/archive`,
      { method: 'POST' }),
}

/**
 * Final-fallback agentic stub used only when /api/chat is unreachable.
 * Mirrors the server's stub mode but also matches against the bundled
 * SAMPLE_STONES so a user typing "round under 5k" still sees inline
 * stone cards, not just text. Once Functions are deployed (Blaze
 * plan) and Gemini key is set, this never fires.
 */
function localStubReply(userText: string): ChatResponse {
  const t = userText.toLowerCase()

  // Try to detect a search-shaped query and run it locally against the
  // bundled fixtures. Lets the deployed site demo agentic results
  // even with no backend.
  const filters: SearchFilters = {}
  const SHAPES = ['round', 'oval', 'pear', 'marquise', 'emerald', 'cushion', 'princess', 'radiant', 'heart', 'asscher']
  const COLORS = ['d', 'e', 'f', 'g', 'h', 'i', 'j', 'k']
  const CLARITIES = ['fl', 'if', 'vvs1', 'vvs2', 'vs1', 'vs2', 'si1', 'si2']
  const shapesMatched = SHAPES.filter((s) => new RegExp(`\\b${s}\\b`).test(t))
  if (shapesMatched.length) filters.shape = shapesMatched.map((s) => s[0].toUpperCase() + s.slice(1))
  const colorsMatched = COLORS.filter((c) => new RegExp(`\\b${c}( |$| color)`).test(t))
  if (colorsMatched.length) filters.color = colorsMatched.map((c) => c.toUpperCase())
  const claritiesMatched = CLARITIES.filter((c) => new RegExp(`\\b${c}\\b`).test(t))
  if (claritiesMatched.length) filters.clarity = claritiesMatched.map((c) => c.toUpperCase())
  const caratMin = t.match(/(\d+(?:\.\d+)?)\s*ct\+|>=?\s*(\d+(?:\.\d+)?)\s*ct/)
  if (caratMin) filters.caratMin = Number(caratMin[1] || caratMin[2])
  const priceMax = t.match(/under\s*\$?\s*(\d+)\s*k?/)
  if (priceMax) filters.priceMax = Number(priceMax[1]) * (priceMax[0].includes('k') ? 1000 : 1)
  const isBiggest = /\bbiggest\b|\blargest\b|\bbig(g|ge)?est\b/.test(t)
  if (isBiggest) filters.sortBy = 'carat_desc'
  const isCheapest = /\bcheapest\b|\bleast expensive\b/.test(t)
  if (isCheapest) filters.sortBy = 'price_asc'
  const stockIds = (userText.match(/\bAJ\d{3,5}\b/gi) || []).map((s) => s.toUpperCase())
  if (stockIds.length) filters.stockIds = stockIds

  const hasSearchSignal =
    !!filters.shape || !!filters.color || !!filters.clarity ||
    typeof filters.caratMin === 'number' || typeof filters.priceMax === 'number' ||
    isBiggest || isCheapest || (filters.stockIds?.length ?? 0) > 0

  if (hasSearchSignal) {
    const filtered = localSearch(SAMPLE_STONES, filters).results
    const top = filtered.slice(0, 12)
    if (top.length === 0) {
      return {
        response: `Couldn't find that exact match in current inventory. Want me to flag a request for the team to source it for you?`,
        results: [],
      }
    }
    const stoneSummary = top.slice(0, 3)
      .map((s) => `#${s.refStockId ?? s.stockId}`)
      .join(', ')
    return {
      response: `Found ${filtered.length} match${filtered.length === 1 ? '' : 'es'}. Top picks: ${stoneSummary}. Tap any stone for full specs and 360° video.`,
      results: top,
      stockIds: top.map((s) => s.stockId),
      actions: [{ label: 'Browse all matches', route: '/search' }],
    }
  }

  if (/\bhi\b|\bhey\b|\bhello\b/.test(t)) {
    return { response: "Hey! I'm DiaEx diamond advisor. Tell me what you're looking for — shape, carat range, color, budget — and I'll surface stones from our inventory." }
  }
  if (/order|invoice|payment/.test(t)) {
    return {
      response: "DiaEx is a directory — once you're verified you see each stone's price and the supplier's direct contact, and you deal with them off-platform (no on-platform orders or invoices). Want me to help you find a stone?",
      actions: [{ label: 'Find a diamond', route: '/search' }],
    }
  }
  if (/sell|supplier/.test(t)) {
    return {
      response: "If you want to list inventory with us, head to /sell — happy to walk you through it.",
      actions: [{ label: 'Sell with DiaEx', route: '/sell' }],
    }
  }
  return { response: "I'm here to help you find diamonds. Tell me what you're looking for — shape, carat range, color, clarity, budget — or describe an occasion." }
}

/**
 * Share-with-markup (#40). create() is authed (Firebase token attached);
 * get() is public (anonymous) so a shared link opens for a non-user customer.
 */
export const sharesApi = {
  create: (payload: {
    stockIds: string[]
    markupPercent?: number
    // 'supplier' (default) marks up the raw supplier price; 'aska' marks up the
    // Aska price (supplier + concierge fee). #57.
    markupBase?: 'supplier' | 'aska'
    showStockId?: boolean
    title?: string
    note?: string
  }): Promise<{ shareId: string; count: number }> =>
    api('/shares', { method: 'POST', body: payload }),
  get: (id: string): Promise<ShareView> =>
    api(`/shares/${encodeURIComponent(id)}`, { anonymous: true }),
  // The signed-in user's own shares — for the "My shares" page.
  mine: (): Promise<{ shares: ShareSummary[] }> =>
    api('/shares/mine'),
  remove: (id: string): Promise<{ deleted: boolean }> =>
    api(`/shares/${encodeURIComponent(id)}`, { method: 'DELETE' }),
}

/**
 * The PUBLIC supplier-facing RFQ page (/rfq/:token). Anonymous — a supplier opens
 * the WhatsApp'd link without signing in. The token is a sealed sourcing-thread id;
 * the response is buyer-masked (only the stones we asked them about + destination).
 */
export const rfqApi = {
  get: (token: string): Promise<{
    ok: boolean
    rfq: {
      stones: { stockId: string; spec: string; listedPerCarat: number | null }[]
      destinationCountry: string | null
    }
  }> => api(`/rfq/${encodeURIComponent(token)}`, { anonymous: true }),
}

export interface ShareSummary {
  shareId: string
  count: number
  markupPercent: number
  markupBase: 'supplier' | 'aska'
  showStockId: boolean
  title: string | null
  createdAt: number
}

/**
 * Open-marketplace: log a buyer→supplier enquiry when a contact CTA is tapped.
 * Fire-and-forget — never blocks the tel:/mailto: navigation; no-ops server-side
 * when the flag is off / caller is anon.
 */
export function logEnquiry(
  stockId: string,
  channel: 'call' | 'whatsapp' | 'email',
  uniqueId?: number | null,
): void {
  const body = uniqueId != null ? { id: String(uniqueId), channel } : { stockId, channel }
  void api('/enquiries', { method: 'POST', body }).catch(() => {})
}
